// src/App.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import './App.css';
import promediaService from './services/promediaService';
import { canAccessScope, canAccessCap, isAdminUser } from './permissions';

import Login from './components/Login';
import AdminPanel from './components/AdminPanel';
import FileManager from './components/FileManager';
import GroupManager from './components/GroupManager';
import LiveStream from './components/LiveStream';
import Sidebar from './components/Sidebar';
import webdavService from './services/webdavService';
import CachedGridThumbnail from './components/CachedGridThumbnail';
import axios from 'axios';
import RemoteControlModal from './components/RemoteControlModal';
import DeviceQuickManageModal from './components/DeviceQuickManageModal';
import TimelineBackup from './components/TimelineBackup';
import { getRuntimeConfig } from './config';
window.webdavService = webdavService;

const { API_BASE_URL } = getRuntimeConfig();

const deviceFirstFileCache = new Map();

// Global axios interceptor for WebDAV
axios.interceptors.request.use((req) => {
  const token = localStorage.getItem('token');
  if (token) {
    req.headers.Authorization = `Bearer ${token}`;
  }
  return req;
}, error => {
  return Promise.reject(error);
});

// Custom component to handle authenticated image loading
const PreviewImage = ({ src, alt, className, token, onLoad, onError }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        console.log('?? Loading preview image with auth:', src);
        
        const response = await fetch(src, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'image/*'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setImageSrc(imageUrl);
        setLoading(false);
        if (onLoad) onLoad();
        
      } catch (error) {
        console.error('? Preview image load error:', error);
        setLoading(false);
        if (onError) onError();
      }
    };

    if (src && token) {
      loadImage();
    }

    return () => {
      if (imageSrc) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [src, token]);

  if (loading) {
    return <div className="preview-loading">Loading preview...</div>;
  }

  if (!imageSrc) {
    return <div className="preview-error">Failed to load preview</div>;
  }

  return <img src={imageSrc} alt={alt} className={className} />;
};

// Simplified Video Preview Component
const VideoPreview = React.memo(({ pathForFetch, filename, className, deviceName, isModal = false }) => {
  const [thumbnailSrc, setThumbnailSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const loadThumbnail = async () => {
      if (!deviceName || !filename) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token');
        }

        console.log(`?? Loading video thumbnail - Modal: ${isModal}, Device: ${deviceName}, File: ${filename}`);
        
        const url = `${API_BASE_URL}/api/thumbnail/${encodeURIComponent(deviceName)}/${encodeURIComponent(filename)}`;
        
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!isMounted) return;
        
        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          
          if (isMounted) {
            setThumbnailSrc(blobUrl);
            console.log(`? Video thumbnail loaded`);
          }
        } else {
          throw new Error(`HTTP ${response.status}`);
        }
        
        if (isMounted) {
          setLoading(false);
        }
        
      } catch (err) {
        console.error('? Video thumbnail error:', err);
        if (isMounted) {
          setError('Failed to load video preview');
          setLoading(false);
        }
      }
    };

    loadThumbnail();
    
    return () => {
      isMounted = false;
      if (thumbnailSrc) {
        URL.revokeObjectURL(thumbnailSrc);
      }
    };
  }, [deviceName, filename, isModal]);

  const handleDownload = () => {
    window.open(`${API_BASE_URL}${pathForFetch}`, '_blank');
  };

  // In modal mode the image itself drives the size (viewport-relative max-width/height).
  // Don't use width/height:100% here — the flex parent has no explicit height.
  const containerSize = isModal ? {} : {
    width: '200px',
    height: '150px'
  };

  const buttonSize = isModal ? {
    padding: '12px 24px',
    fontSize: '16px'
  } : {
    padding: '8px 16px',
    fontSize: '12px'
  };

  const nonModalBox = {
    width: containerSize.width || '200px',
    height: containerSize.height || '150px',
  };

  if (loading) {
    return (
      <div className={className} style={{
        ...(isModal ? { padding: '40px 60px', color: '#888', fontSize: '14px' } : {
          ...nonModalBox,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          border: '1px solid #ddd',
          borderRadius: '8px',
        })
      }}>
        {isModal ? 'Loading…' : <div style={{ textAlign: 'center', color: '#666' }}>Loading…</div>}
      </div>
    );
  }

  if (error || !thumbnailSrc) {
    return (
      <div className={className} style={{
        ...(isModal ? { padding: '40px 60px', color: '#888', fontSize: '14px', textAlign: 'center' } : {
          ...nonModalBox,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#333',
          border: '1px solid #ddd',
          borderRadius: '8px',
          color: 'white',
          textAlign: 'center',
        })
      }}>
        <div style={{ fontSize: isModal ? '32px' : '24px', marginBottom: '10px' }}>🎬</div>
        <div style={{ fontSize: isModal ? '14px' : '12px' }}>
          {error || 'Failed to load video preview'}
        </div>
        {!isModal && (
          <button
            onClick={handleDownload}
            style={{
              ...buttonSize,
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              marginTop: '10px',
            }}
          >
            Download Video
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={className} style={{
      ...containerSize,
      ...(isModal ? {} : {
        position: 'relative',
        backgroundColor: '#000',
        border: '1px solid #ddd',
        borderRadius: '8px',
        overflow: 'hidden',
        margin: '0 auto'
      })
    }}>
      <img
        src={thumbnailSrc}
        alt="Video preview"
        style={isModal ? {
          display: 'block',
          width: 'min(96vw, 1400px)',
          height: 'calc(96vh - 46px)',
          objectFit: 'contain',
        } : {
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block'
        }}
      />
    </div>
  );
});

// Memoized Device Card Component with proper comparison
const DeviceCard = React.memo((props) => {
  const {
    device,
    user,
    onThumbnailClick,
    onRowClick,
    onDeviceNameClick,
    truncateFilename,
    formatDate,
    formatMediaTime,
    formatBytes,
    calculateStoragePercentage,
    getMediaType,
    showActionsMenu,
    setShowActionsMenu,
    // Add missing props
    filteredDevices,
    setDeviceIndex,
    setSelectedDeviceState,
    setShowDeviceModal,
    setIsEditingDevice,
    setIsAddingDevice,
    onOwnerFilterClick,
    ownerLogoMap = {},
    isHighlighted = false,
  } = props;
  // Scroll the first highlighted card into view when the highlight turns on.
  const cardRef = React.useRef(null);
  React.useEffect(() => {
    if (isHighlighted && cardRef.current) {
      // Small delay so the user-section scroll finishes first
      const t = setTimeout(() => {
        try {
          cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        } catch {}
      }, 250);
      return () => clearTimeout(t);
    }
  }, [isHighlighted]);

  const [folderFirstFile, setFolderFirstFile] = useState(null);

  const { datePart, timePart } = useMemo(() => {
    if (!device.last_seen) return { datePart: 'N/A', timePart: '' };
    try {
      const date = new Date(device.last_seen);
      if (isNaN(date.getTime())) return { datePart: 'Invalid Date', timePart: '' };

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const formattedDate = `${day}/${month}/${year}`;

      const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
      const formattedTime = date.toLocaleTimeString('en-US', timeOptions);

      return { datePart: formattedDate, timePart: formattedTime };
    } catch (e) {
      console.error("Error formatting timestamp:", device.last_seen, e);
      return { datePart: 'Format Error', timePart: '' };
    }
  }, [device.last_seen]);

  const mediaTime = useMemo(() => formatMediaTime(device.last_displayed_file_time), [device.last_displayed_file_time, formatMediaTime]);
  const isFrpOnline = useMemo(() => {
    const v = device?.is_frp_online;
    return v === true || v === 1 || v === '1' || v === 'true' || v === 't' || v === 'yes';
  }, [device?.is_frp_online]);
  const isOnline = device.is_online;

  useEffect(() => {
    let cancelled = false;

    const hasLastDisplayed = !!device.last_displayed_file;
    const hasFallback = !!device.fallback_file;
    const canLoadFromFolder = !hasLastDisplayed && !hasFallback && !!device.media_files_link;

    if (!canLoadFromFolder) {
      setFolderFirstFile(null);
      return () => { cancelled = true; };
    }

    const cacheKey = `${device.device_name}::${device.media_files_link}`;
    const cached = deviceFirstFileCache.get(cacheKey);
    if (cached && Date.now() - cached.at < 5 * 60 * 1000) {
      setFolderFirstFile(cached.file || null);
      return () => { cancelled = true; };
    }

    (async () => {
      try {
        const files = await promediaService.getDeviceFiles(device.media_files_link);
        const file = Array.isArray(files) && files.length > 0 ? files[0] : null;
        deviceFirstFileCache.set(cacheKey, { at: Date.now(), file });
        if (!cancelled) setFolderFirstFile(file);
      } catch (e) {
        deviceFirstFileCache.set(cacheKey, { at: Date.now(), file: null });
        if (!cancelled) setFolderFirstFile(null);
      }
    })();

    return () => { cancelled = true; };
  }, [device.device_name, device.media_files_link, device.last_displayed_file, device.fallback_file]);

  const handleRowClickMemo = useCallback(() => {
    onRowClick(device);
  }, [device, onRowClick]);

  const handleDeviceHeaderClickMemo = useCallback((e) => {
    console.log('?? Header clicked! User is admin:', user?.is_admin);
    
    // Only show actions menu for admin users
    if (user?.is_admin) {
      e.stopPropagation();
      
      if (showActionsMenu === device.device_name) {
        console.log('?? Closing menu for:', device.device_name);
        setShowActionsMenu(null);
      } else {
        console.log('?? Opening menu for:', device.device_name);
        
        // Store click position for menu positioning
        const rect = e.currentTarget.getBoundingClientRect();
        window.menuPosition = {
          top: rect.bottom + 5,
          left: rect.left,
          deviceName: device.device_name
        };
        
        setShowActionsMenu(device.device_name);
      }
    }
  }, [device.device_name, showActionsMenu, setShowActionsMenu, user]);

  const handleMediaRowClickMemo = useCallback(() => {
    // Open remote control modal for media row click
    onDeviceNameClick(device);
  }, [device, onDeviceNameClick]);

  // Get status color for device name and flag
  const statusColor = useMemo(() => {
    if (!isOnline) return '#dc3545'; // Red for offline
    if (isFrpOnline) return 'var(--primary-dark)'; // Dark blue for both online (FRP)
    return 'var(--primary-color)'; // Primary blue for device only online
  }, [isOnline, isFrpOnline]);

  return (
    <div ref={cardRef}
         className={`device-card ${isOnline ? 'online' : 'offline'} ${isFrpOnline ? 'frp-online' : ''} ${user?.is_admin ? 'admin-row' : 'user-row'}`}
         style={isHighlighted ? {
           // Pulse-ring highlight when admin clicks "TVs" on a branch card
           boxShadow: '0 0 0 4px #22C55E, 0 0 24px rgba(34,197,94,0.4)',
           animation: 'pmHighlightPulse 1.2s ease-in-out infinite',
           transition: 'box-shadow 0.3s',
         } : { transition: 'box-shadow 0.3s' }}>
      {/* Left colored flag */}
      <div className="device-card-flag" style={{ backgroundColor: statusColor }}></div>
      
      {/* Header row - Device name and owner/folder - CLICKABLE FOR ALL USERS */}
      <div
        className="device-card-header clickable-header"
        onClick={(e) => {
          e.stopPropagation();

          if (user?.is_admin) {
            // Admin users: Show actions menu
            handleDeviceHeaderClickMemo(e);
          } else {
            // Regular users: Show read-only device details modal
            console.log('?? Opening Device Details for user:', device.device_name);

            // Find device index for navigation
            const index = filteredDevices.findIndex(d => d.device_name === device.device_name);
            setDeviceIndex(index);

            // Set device data for modal
            setSelectedDeviceState(device);

            // Show device info modal (read-only mode)
            setShowDeviceModal(true);
            setIsEditingDevice(false);
            setIsAddingDevice(false);
          }
        }}
        style={{
          cursor: 'pointer',
          backgroundColor: isOnline ? 'var(--header-bg-online)' : 'var(--header-bg-offline)',
          background: isOnline ? 'var(--header-bg-online)' : 'var(--header-bg-offline)',
          backgroundImage: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px 8px 20px',
          borderBottom: isOnline ? '1px solid rgba(47, 128, 237, 0.2)' : '1px solid rgba(220, 53, 69, 0.2)'
        }}
        title="Player Info"
      >
        <div 
          className="device-name-container"
          style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '2px',
            flex: 1
          }}
        >
          <div 
            className="device-name"
            style={{ color: statusColor, fontWeight: '700', fontSize: '16px' }}
            title={user?.is_admin ? "Click for actions menu" : "Click to view details"}
          >
            {device.device_name}
          </div>
          
          {/* Show description under device name */}
          {device.description && (
            <div 
              className="device-description"
              style={{ 
                color: '#666', 
                fontWeight: '300', 
                fontSize: '11px',
                opacity: '0.8'
              }}
            >
              {device.description}
            </div>
          )}
        </div>
        
        {user?.is_admin ? (
          <div style={{ textAlign: 'right' }}>
            <div
              className="device-owner"
              style={{ fontSize: '12px', color: '#666', fontWeight: 'normal',
                       backgroundColor: 'rgba(0,0,0,0.06)', padding: '3px 8px 3px 4px',
                       borderRadius: '4px', border: '1px solid rgba(0,0,0,0.08)',
                       display: 'inline-flex', alignItems: 'center', gap: 5 }}
            >
              {/* Brand logo or initials circle */}
              {(() => {
                // The badge names the brand the screen belongs to. The logo
                // now arrives resolved on the device row; ownerLogoMap stays
                // as a fallback for rows served before the join landed.
                const owner = device.brand_name || device.owner_username;
                const logo = device.brand_logo || (owner && ownerLogoMap[owner]);
                const initials = owner ? owner.slice(0, 2).toUpperCase() : '??';
                const colors = ['#4CAF50','#2196F3','#FF9800','#9C27B0','#F44336','#00BCD4','#795548'];
                const bg = colors[(owner || '').charCodeAt(0) % colors.length] || '#607D8B';
                return logo ? (
                  <img src={logo} alt={owner}
                    style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'contain',
                             background: '#fff', border: '1px solid #ddd', flexShrink: 0 }} />
                ) : (
                  <span style={{ width: 20, height: 20, borderRadius: '50%', background: bg,
                                 color: '#fff', fontSize: 9, fontWeight: 700, flexShrink: 0,
                                 display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                    {initials}
                  </span>
                );
              })()}
              {device.brand_name || 'No Brand'}
            </div>
            <div
              className="device-branch"
              style={{ color: '#000', fontWeight: '300', fontSize: '11px',
                       opacity: 0.7, marginTop: '2px' }}
            >
              {(() => {
                if (!device.media_files_link) return 'Folder';
                try {
                  const parts = new URL(device.media_files_link).pathname
                    .replace('/home/', '').replace(/\/$/, '').split('/').filter(Boolean);
                  if (parts.length >= 2) return parts[parts.length - 2];
                  if (parts.length === 1) return 'Main';
                } catch { /* fall */ }
                return 'Folder';
              })()}
            </div>
          </div>
        ) : (
          <div
            className="device-subfolder"
            style={{ fontSize: '12px', color: '#666', fontWeight: 'normal',
                     backgroundColor: 'rgba(0,0,0,0.06)', padding: '4px 8px',
                     borderRadius: '4px', border: '1px solid rgba(0,0,0,0.08)' }}
          >
            {(() => {
              if (!device.media_files_link) return 'Folder';
              try {
                const parts = new URL(device.media_files_link).pathname
                  .replace('/home/', '').replace(/\/$/, '').split('/').filter(Boolean);
                if (parts.length >= 2) return parts[parts.length - 2];
                if (parts.length === 1) return 'Main';
              } catch { /* fall */ }
              return 'Folder';
            })()}
          </div>
        )}
      </div>

      {/* Media row - Thumbnail and media info - CLICKABLE */}
      <div
        className="device-card-media clickable-media"
        onClick={handleMediaRowClickMemo}
        title="Remote Control"
      >
        <div className="media-thumbnail-container">
          {(() => {
            const hasLastDisplayed = device.last_displayed_file;
            const hasFallback = device.fallback_file;
            const displayFile = hasLastDisplayed || hasFallback || folderFirstFile?.basename;
            
            if (displayFile && device.media_files_link) {
              let fileToUse;
              
              if (hasLastDisplayed) {
                fileToUse = device.last_displayed_file;
              } else if (hasFallback) {
                if (device.fallback_file_path) {
                  fileToUse = device.fallback_file_path;
                } else {
                  fileToUse = device.fallback_file;
                }
              } else if (folderFirstFile?.basename) {
                fileToUse = folderFirstFile.basename;
              }
              
              
              const thumbnailPath = fileToUse;
              const isVideo = hasLastDisplayed 
                ? getMediaType(device.last_displayed_file) === 'video'
                : (hasFallback && device.fallback_file_is_video);
              
              
              // Use a stable key based on the actual file, not timestamp
              const thumbnailKey = `${device.device_name}-${fileToUse}`;
              
              return (
                <div className="media-thumbnail">
                  <CachedGridThumbnail
                    itemPath={thumbnailPath}
                    isVideo={isVideo}
                    isPdf={getMediaType(fileToUse) === 'pdf'}
                    customBaseUrl={`${window.location.protocol}//${window.location.hostname}`}
                    deviceName={device.device_name}
                    key={thumbnailKey}
                  />
                </div>
              );
            } else {
              return <div className="no-thumbnail">No media</div>;
            }
          })()}
        </div>

        <div className="media-info">
          {(() => {
            const hasLastDisplayed = device.last_displayed_file;
            const hasFallback = device.fallback_file;
            const displayFile = hasLastDisplayed || hasFallback || folderFirstFile?.basename;
            
            if (displayFile) {
              return (
                <>
                  <div className="media-filename">
                    {truncateFilename(displayFile, 20)}
                  </div>
                  {hasLastDisplayed && device.last_displayed_file_time ? (
                    <div className="media-time">
                      {formatDate(device.last_displayed_file_time)} {mediaTime}
                    </div>
                  ) : hasFallback && device.fallback_file_modified ? (
                    <div className="media-time">
                      {formatDate(device.fallback_file_modified)} <i>(registered)</i>
                    </div>
                  ) : device.register_date ? (
                    <div className="media-time">
                      {formatDate(device.register_date)} <i>(registered)</i>
                    </div>
                  ) : null}
                </>
              );
            } else {
              return (
                <>
                  <div className="media-filename">{device.register_date ? 'N/A' : 'N/A'}</div>
                  {device.register_date && (
                    <div className="media-time">
                      {formatDate(device.register_date)} <i>(registered)</i>
                    </div>
                  )}
                </>
              );
            }
          })()}
        </div>
      </div>

      {/* Storage row */}
      <div
        className="device-card-storage"
        onClick={(e) => {
          e.stopPropagation();
          handleRowClickMemo();
        }}
        style={{ cursor: 'pointer' }}
        title="Player Files"
      >
        <div className="storage-info-card">
          <div className="storage-text-line">
            <span className="storage-free">{formatBytes(device.storage_space_free)} Free</span>
            <span className="storage-total">{formatBytes(device.storage_space_total)} Total</span>
          </div>
          {device.storage_space_free != null && device.storage_space_total != null && (
            <div className="storage-bar-container">
              <div
                className={`storage-bar-used ${calculateStoragePercentage(device.storage_space_free, device.storage_space_total) > 80 ? 'storage-bar-low' : ''}`}
                style={{
                  width: `${calculateStoragePercentage(device.storage_space_free, device.storage_space_total)}%`
                }}
              ></div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function - return true if props are equal (skip re-render)
  const prevDevice = prevProps.device;
  const nextDevice = nextProps.device;
  
  // Check if the important fields have changed
  const isEqual = (
    prevDevice.device_name === nextDevice.device_name &&
    prevDevice.is_online === nextDevice.is_online &&
    prevDevice.is_frp_online === nextDevice.is_frp_online &&
    prevDevice.last_displayed_file === nextDevice.last_displayed_file &&
    prevDevice.last_displayed_file_time === nextDevice.last_displayed_file_time &&
    prevDevice.fallback_file === nextDevice.fallback_file &&
    prevDevice.storage_space_free === nextDevice.storage_space_free &&
    prevDevice.storage_space_total === nextDevice.storage_space_total &&
    prevDevice.device_model === nextDevice.device_model &&
    prevDevice.android_version === nextDevice.android_version &&
    prevDevice.ip_address_internal === nextDevice.ip_address_internal &&
    prevDevice.working_hours === nextDevice.working_hours &&
    prevDevice.owner_username === nextDevice.owner_username &&
    prevDevice.brand_name === nextDevice.brand_name &&
    prevDevice.branch_name === nextDevice.branch_name &&
    prevProps.showActionsMenu === nextProps.showActionsMenu &&
    prevProps.user?.is_admin === nextProps.user?.is_admin
  );
  
  return isEqual;
});

function App() {
 console.log('?? App component rendered at', new Date().toLocaleTimeString());

   const {
    initialPath = '',
    isEmbedded = false,
    customBaseUrl = null,
    showTitle = '',
    maxRows = 5,
    selectedDevice,
  } = {};
  const [showRemoteControlModal, setShowRemoteControlModal] = useState(false);
  const [selectedDeviceForRemote, setSelectedDeviceForRemote] = useState(null);
  // Standalone Player Manager (Device Quick Manage) — opened directly from the
  // kebab/card menu without spawning the Remote Control modal underneath.
  const [quickManageDevice, setQuickManageDevice] = useState(null);
  const [devices, setDevices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [user, setUser] = useState(null);
  const lastActivityRef = useRef(Date.now());
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showFileManager, setShowFileManager] = useState(false);
  const [showGroupManager, setShowGroupManager] = useState(false);
  const [showLiveStream, setShowLiveStream] = useState(false);
  const [showTimelineBackup, setShowTimelineBackup] = useState(false);
  const [showMenuManager, setShowMenuManager] = useState(() => {
    try { return sessionStorage.getItem('pm_active_panel') === 'menu'; } catch { return false; }
  });
  const [statusFilter, setStatusFilter] = useState('all');
  const adminPanelRef = useRef(null);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [selectedDeviceState, setSelectedDeviceState] = useState(null);
  const [isAddingDevice, setIsAddingDevice] = useState(false);
  const [isEditingDevice, setIsEditingDevice] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Mobile detection — used to actually omit columns/text from the JSX on
  // small screens, not just hide via CSS. Bypasses any CSS cache weirdness.
  const [isMobileView, setIsMobileView] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 640px)').matches
      : false
  );
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = (e) => setIsMobileView(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange); // older Safari
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  // Players view mode (cards | table). Cards is the default per request.
  // Persisted so admin preference survives reloads.
  const [playersViewMode, setPlayersViewModeRaw] = useState(() => {
    try {
      const v = localStorage.getItem('players_view');
      return v === 'table' ? 'table' : 'cards';
    } catch { return 'cards'; }
  });
  const setPlayersViewMode = (v) => {
    setPlayersViewModeRaw(v);
    try { localStorage.setItem('players_view', v); } catch {}
  };

  // Players table — single-portal 3-line menu, same design as AdminPanel users.
  const [openPlayerRowMenuId, setOpenPlayerRowMenuId] = useState(null);
  const [openPlayerRowMenuPos, setOpenPlayerRowMenuPos] = useState(null);
  const closePlayerRowMenu = useCallback(() => {
    setOpenPlayerRowMenuId(null);
    setOpenPlayerRowMenuPos(null);
  }, []);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);  
  const [ownerFilter, setOwnerFilter] = useState('');
  const [pendingScrollUser, setPendingScrollUser] = useState(null);
  // When admin clicks "TVs (X)" on a branch card, we navigate to the devices
  // page AND highlight only that branch's devices. branchId is set briefly,
  // device cards apply a ring/glow, then it clears so the page returns to
  // normal browsing.
  const [highlightBranchId, setHighlightBranchId] = useState(null);
  const [pendingHighlightBranchId, setPendingHighlightBranchId] = useState(null);
  // 2026-05-19 — unified search: one input + a field selector.
  // searchField: 'name' (device_name) | 'uid' (device_uid) | 'description' | 'register_date'.
  const [searchField, setSearchField] = useState('name');
  const [uniqueOwners, setUniqueOwners] = useState([]);   // Add/Edit modals — still an owner
  const [ownerLogoMap, setOwnerLogoMap] = useState({});  // username -> brand_logo url
  const [brandLabels, setBrandLabels] = useState({});   // username -> {name, nameAr}
  const ownersFetchStateRef = useRef({ inFlight: false, lastFetchAt: 0 });
  const [deviceIndex, setDeviceIndex] = useState(0);
  const [selectedOwner, setSelectedOwner] = useState('');
  // 2026-05-19 Phase C — Add/Edit Device modal: owner→brand→branch cascade
  // dropdowns. allBrands is fetched once when the modal opens (admin scope);
  // availableBranches is refetched whenever the selected brand changes.
  // addingBranch toggles the "+ Add new branch" inline form.
  const [allBrands, setAllBrands] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  const [addingBranch, setAddingBranch] = useState(false);
  const [newBranchName, setNewBranchName] = useState('');
  const [mediaFolderTouched, setMediaFolderTouched] = useState(false);
  // 2026-05-19 — Adopt selector. Lists devices that auto-registered over
  // MQTT but were never claimed by any owner. Admin uses this to pair a
  // cloned placeholder row with the actual physical hardware.
  const [orphanDevices, setOrphanDevices] = useState([]);
  const [adoptingFromUid, setAdoptingFromUid] = useState(false);
  // 2026-05-20 — UID of the most-recently-adopted device. Used to render
  // a "✓ Adoption succeeded" badge next to the UID line in the Edit modal
  // for the same session only. Cleared on next modal open.
  const [justAdoptedUid, setJustAdoptedUid] = useState(null);
  const [deviceForFileManagerModal, setDeviceForFileManagerModal] = useState(null); // Renamed state

  // Persist active panel so refresh restores the same view
  useEffect(() => {
    try {
      if (showMenuManager) sessionStorage.setItem('pm_active_panel', 'menu');
      else if (sessionStorage.getItem('pm_active_panel') === 'menu') sessionStorage.removeItem('pm_active_panel');
    } catch {}
  }, [showMenuManager]);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const thumbnailCache = useRef(new Map()); // Add thumbnail cache
  const deviceUpdateTimes = useRef(new Map()); // Add update throttling
  const [pendingDeviceInfoRefresh, setPendingDeviceInfoRefresh] = useState(false);
  // Enhanced system data state
  const [systemData, setSystemData] = useState({
  storage: { used: 0.5, total: 4.3, free: 3.8, percentage: 11.6 },
  ram: { used: 1.2, total: 3.8, free: 2.6, percentage: 31.6 },
  cpu: { usage: 15.2, load1: 0.45, cores: 4 }
});

  const [emptyCount, setEmptyCount] = useState(0);

  // 2026-05-20 — New-device alert state. `noOwnerAck` is the last count
  // admin acknowledged (persisted across reloads via localStorage); we
  // compare current no-owner count against it and flash a red-pulsing
  // chip in the Players Status card when current > ack.
  const [noOwnerAck, setNoOwnerAck] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('pm_no_owner_ack') || '0', 10);
      return Number.isFinite(v) ? v : 0;
    } catch { return 0; }
  });
  const ackNoOwner = useCallback((n) => {
    setNoOwnerAck(n);
    try { localStorage.setItem('pm_no_owner_ack', String(n)); } catch {}
  }, []);

  // ── Section-tab navigation ──────────────────────────────────────────────────
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const tabsBarRef          = useRef(null);  // the sticky tabs bar element
  const scrollerRef         = useRef(null);  // the inner .tabs-scroll-area
  const tabButtonRefs       = useRef([]);    // each tab <button> element
  const sectionRefs         = useRef([]);    // each group section <div>
  const isScrollingToSection = useRef(false); // block IO while animating
  const scrollingTimerRef    = useRef(null);
  // Drag state — kept in a ref so handlers added on document don't see stale closures
  const dragRef             = useRef({ down: false, startX: 0, startScrollLeft: 0, moved: 0 });

  // Fetch brand display names for admin device owner tabs
  useEffect(() => {
    if (!user || !user.is_admin) return;
    axios.get('/api/admin/owner-brand-labels', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }).then(r => setBrandLabels(r.data)).catch(() => {});
  }, [user]);

  // 2026-05-18 (re-applied after rollback) — `pro_features` column was
  // dropped in Phase E. Source of truth is `user.access` (the hierarchical
  // JSONB tree returned by /api/auth/profile). This shim translates the
  // legacy flat feature names existing callers still use into the new
  // (scope, cap) lookups so we don't have to touch every callsite at once.
  //   'tvs'     → scope 'tv' (any cap)        — sidebar "show TV section?"
  //   'groups'  → tv/groups
  //   'info_screen' → tv/info_screen
  //   'menu'    → scope 'menu' (any cap)
  //   'pos'     → scope 'pos'
  const hasProFeature = useCallback((feature) => {
    if (!user) return false;
    if (isAdminUser(user)) return true;
    switch (feature) {
      case 'tvs':     return canAccessScope(user, 'tv');
      case 'groups':  return canAccessCap(user, 'tv', 'groups');
      case 'info_screen': return canAccessCap(user, 'tv', 'info_screen');
      case 'menu':    return canAccessScope(user, 'menu');
      case 'live_stream': return canAccessScope(user, 'live_stream');
      case 'pos':     return canAccessScope(user, 'pos');
      default:        return false;
    }
  }, [user]);

  useEffect(() => {
    if (!showDeviceModal || !selectedDeviceState?.device_name) return;

    const latest = devices.find(d => d.device_name === selectedDeviceState.device_name);
    if (!latest) return;

    setSelectedDeviceState(prev => {
      if (!prev) return latest;
      return { ...prev, ...latest };
    });
  }, [devices, showDeviceModal, selectedDeviceState?.device_name]);

// Handle scroll to show/hide scroll-to-top button
useEffect(() => {
  const sc = document.querySelector('.sb-main') || window;
  const handleScroll = () => {
    const pos = sc === window ? window.pageYOffset : sc.scrollTop;
    setShowScrollTop(pos > 300);
  };
  sc.addEventListener('scroll', handleScroll, { passive: true });
  return () => sc.removeEventListener('scroll', handleScroll);
}, []);

// Fetch and calculate days remaining to Certbot expiry from backend
useEffect(() => {
  let isMounted = true; // Flag to prevent state updates on unmounted component

  const fetchAndCalculateExpiry = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        if (isMounted) setDaysToCertExpiry(null); // Clear if no token
        return;
      }

      // Check if user is admin before fetching (backend also checks)
      if (!user || !user.is_admin) {
        if (isMounted) setDaysToCertExpiry(null);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/cert-expiry`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!isMounted) return; // Exit if component unmounted during fetch

      if (!response.ok) {
        console.error(`Error fetching cert expiry: HTTP ${response.status}`);
        setDaysToCertExpiry(null);
        return;
      }

      const data = await response.json();
      const expiryDate = new Date(data.expiryDate); // Get expiry date from backend

      if (isNaN(expiryDate.getTime())) {
        console.error('Invalid expiry date received from backend:', data.expiryDate);
        setDaysToCertExpiry(null);
        return;
      }

      const now = new Date();

      // Set both dates to UTC for accurate day difference calculation
      const utcExpiry = Date.UTC(expiryDate.getFullYear(), expiryDate.getMonth(), expiryDate.getDate());
      const utcNow = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());

      const oneDay = 1000 * 60 * 60 * 24; // milliseconds in a day
      const remainingDays = Math.ceil((utcExpiry - utcNow) / oneDay);
      
      if (isMounted) {
        setDaysToCertExpiry(remainingDays);
      }

    } catch (error) {
      console.error('Failed to fetch certificate expiry:', error);
      if (isMounted) setDaysToCertExpiry(null); // Clear on error
    }
  };

  // Fetch immediately on mount and when user/token changes
  fetchAndCalculateExpiry();

  // Recalculate every hour
  const intervalId = setInterval(fetchAndCalculateExpiry, 3600000); // 1 hour

  return () => {
    isMounted = false; // Cleanup flag
    clearInterval(intervalId);
  };
}, [user]); // Re-run when user object changes (e.g., login/logout)

// Auto-logout handler for expired tokens
const handleAuthError = useCallback(() => {
  console.error('?? Authentication failed - logging out user');
  
  // Clear all stored data
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('webdavCredentials');
  
  // Reset user state
  setUser(null);
  
  // Show error message
  setError('Your session has expired. Please login again.');
  
  // Disconnect WebSocket if connected
  if (wsRef.current) {
    wsRef.current.close();
    wsRef.current = null;
  }
  if (window.wsConnection) {
    window.wsConnection.close();
    delete window.wsConnection;
  }
  
  // Call logout API
  fetch(`${API_BASE_URL}/api/auth/logout`, {
    method: 'POST',
    credentials: 'include'
  }).catch(err => console.error('Logout error:', err));
}, []);

// Fetch enhanced system info function
const fetchSystemInfo = useCallback(async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/system-info`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      credentials: 'include'
    });
    
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        handleAuthError();
        return;
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('?? System info updated:', data);
    setSystemData(data);
  } catch (err) {
    console.error("Error fetching system info:", err);
    // Keep using default values on error
  }
}, [handleAuthError]);
  
// Fetch only the count of empty devices (admin only)
const fetchEmptyDevicesCount = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    if (!token || !user || !user.is_admin) {
      setEmptyCount(0);
      return;
    }
    const res = await fetch(`${API_BASE_URL}/api/admin/empty-devices`, {
      headers: { 'Authorization': `Bearer ${token}` },
      credentials: 'include'
    });

    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        handleAuthError();
        return;
      }
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    setEmptyCount(Number(data.emptyCount || 0));
  } catch (e) {
    console.warn('Failed to fetch empty devices count:', e.message);
    // keep previous value
  }
}, [user, handleAuthError]);


  // Scroll to top function
const scrollToTop = () => {
  const sc = document.querySelector('.sb-main') || window;
  sc.scrollTo({ top: 0, behavior: 'smooth' });
};
  // WebSocket connection state
  const [wsConnection, setWsConnection] = useState(null);
  const wsRef = useRef(null);
  const [daysToCertExpiry, setDaysToCertExpiry] = useState(null);

  // Form state for adding/editing devices. 2026-05-19 Phase C — brand_id /
  // branch_id now carried in the form. Empty string means "unassigned"; the
  // POST/PUT handlers translate '' → null before sending to the backend.
  const [deviceFormData, setDeviceFormData] = useState({
    device_name: '',
    description: '',
    register_date: new Date().toISOString().split('T')[0],
    anydesk_id: '',
    anydesk_password: '',
    media_files_link: '',
    brand_id: '',
    branch_id: '',
  });

  // Ref for clicking outside actions menu
  const actionsMenuRef = useRef(null);

  // Optimized device update handler (DEFINED FIRST)
const handleDeviceUpdate = useCallback((updatedDevice) => {
  const deviceName = updatedDevice.device_name;
  const now = Date.now();
  const lastUpdate = deviceUpdateTimes.current.get(deviceName) || 0;
  
  setDevices(prevDevices => {
    const existingDeviceIndex = prevDevices.findIndex(
      device => device.device_name === updatedDevice.device_name
    );

    if (existingDeviceIndex !== -1) {
      const existingDevice = prevDevices[existingDeviceIndex];
      const incomingMedia = updatedDevice.last_displayed_file;
      const incomingMediaTime = updatedDevice.last_displayed_file_time;
      const incomingOnline = updatedDevice.is_online;
      const incomingLastSeen = updatedDevice.last_seen;
      const shouldUpdateMedia = incomingMedia !== undefined && incomingMedia !== null && incomingMedia !== '';
      const shouldUpdateMediaTime = incomingMediaTime !== undefined && incomingMediaTime !== null && incomingMediaTime !== '';
      const shouldUpdateStatus = incomingOnline !== undefined && incomingOnline !== null;
      const shouldUpdateLastSeen = incomingLastSeen !== undefined && incomingLastSeen !== null && incomingLastSeen !== '';
      const incomingStorageFree = updatedDevice.storage_space_free;
      const incomingStorageTotal = updatedDevice.storage_space_total;
      const shouldUpdateStorageFree = incomingStorageFree !== undefined && incomingStorageFree !== null && incomingStorageFree !== '';
      const shouldUpdateStorageTotal = incomingStorageTotal !== undefined && incomingStorageTotal !== null && incomingStorageTotal !== '';
      
      // Only update if there are VERY significant changes
      const statusChanged = shouldUpdateStatus && existingDevice.is_online !== incomingOnline;
      const mediaChanged = shouldUpdateMedia && existingDevice.last_displayed_file !== incomingMedia;
      const mediaTimeChanged =
        shouldUpdateMediaTime &&
        Number.isFinite(new Date(incomingMediaTime).getTime()) &&
        (!Number.isFinite(new Date(existingDevice.last_displayed_file_time).getTime()) || new Date(incomingMediaTime).getTime() > new Date(existingDevice.last_displayed_file_time).getTime());
      const storageChanged = (shouldUpdateStorageFree || shouldUpdateStorageTotal) && (
        Math.abs((existingDevice.storage_space_free || 0) - (Number(incomingStorageFree) || 0)) > 50000000 ||
        Math.abs((existingDevice.storage_space_total || 0) - (Number(incomingStorageTotal) || 0)) > 50000000
      ); // 50MB threshold
      const infoChanged = (
        (updatedDevice.device_model && updatedDevice.device_model !== existingDevice.device_model) ||
        (updatedDevice.android_version && updatedDevice.android_version !== existingDevice.android_version) ||
        (updatedDevice.ip_address_internal && updatedDevice.ip_address_internal !== existingDevice.ip_address_internal) ||
        (updatedDevice.working_hours && updatedDevice.working_hours !== existingDevice.working_hours)
      );
      
      if (!statusChanged && !mediaChanged && !mediaTimeChanged && !storageChanged && !infoChanged) {
        return prevDevices; // No significant changes, prevent re-render
      }
      
      // Only mark an update time when we actually apply a meaningful patch.
      deviceUpdateTimes.current.set(deviceName, now);
      
      // Create new array with minimal updates
      const newDevices = [...prevDevices];
      newDevices[existingDeviceIndex] = {
        ...existingDevice,
        ...(statusChanged && { is_online: incomingOnline }),
        ...((statusChanged || shouldUpdateLastSeen) && shouldUpdateLastSeen && { last_seen: incomingLastSeen }),
        ...((mediaChanged || mediaTimeChanged) && {
          ...(mediaChanged ? { last_displayed_file: incomingMedia } : {}),
          ...(shouldUpdateMediaTime ? { last_displayed_file_time: incomingMediaTime } : {})
        }),
        ...(storageChanged && {
          ...(shouldUpdateStorageFree ? { storage_space_free: Number(incomingStorageFree) } : {}),
          ...(shouldUpdateStorageTotal ? { storage_space_total: Number(incomingStorageTotal) } : {})
        }),
        ...(infoChanged && {
          ...(updatedDevice.device_model ? { device_model: updatedDevice.device_model } : {}),
          ...(updatedDevice.android_version ? { android_version: updatedDevice.android_version } : {}),
          ...(updatedDevice.ip_address_internal ? { ip_address_internal: updatedDevice.ip_address_internal } : {}),
          ...(updatedDevice.working_hours ? { working_hours: updatedDevice.working_hours } : {})
        })
      };
      return newDevices;
    }
    
    return prevDevices;
  });
}, []);

  const applyDeviceInfoResultToState = useCallback((deviceName, result) => {
    if (!deviceName || !result || typeof result !== 'object') return;

    const toNumberOrNull = (v) => {
      if (v === undefined || v === null) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const toUptimeMsOrNull = (msSecondsOrEpoch) => {
      if (msSecondsOrEpoch === undefined || msSecondsOrEpoch === null) return null;
      const n = Number(msSecondsOrEpoch);
      if (!Number.isFinite(n)) return null;

      // If it looks like an epoch timestamp in ms, convert to elapsed uptime.
      if (n > 1e12) {
        const elapsed = Date.now() - n;
        return elapsed > 0 ? elapsed : null;
      }

      // If it looks like a duration in ms.
      if (n > 1e9) return n;

      // Otherwise assume seconds.
      return n * 1000;
    };

    const formatDuration = (ms) => {
      if (ms === undefined || ms === null) return null;
      const totalSeconds = Math.max(0, Math.floor(ms / 1000));
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const hh = String(hours).padStart(2, '0');
      const mm = String(minutes).padStart(2, '0');
      const ss = String(seconds).padStart(2, '0');
      return `${days}d, ${hh}:${mm}:${ss}`;
    };

    const derivedWorkingHours =
      (result.working_hours ?? result.workingHours ?? null) ??
      formatDuration(toUptimeMsOrNull(result.appUptime ?? result.uptime ?? null));

    const patch = {
      device_name: deviceName,
      device_model: (result.device_model ?? result.deviceModel ?? result.model ?? result.hardwareModel ?? null),
      android_version: (result.android_version ?? result.androidVersion ?? null),
      ip_address_internal: (result.ip_address_internal ?? result.ipAddressInternal ?? result.ipAddress ?? null),

      last_displayed_file: (result.last_displayed_file ?? result.lastDisplayedFile ?? null),

      working_hours: derivedWorkingHours,
      storage_space_total: toNumberOrNull(result.storage_space_total ?? result.storageSpaceTotal ?? null),
      storage_space_free: toNumberOrNull(result.storage_space_free ?? result.storageSpaceFree ?? null)
    };

    setDevices(prevDevices => {
      const idx = (prevDevices || []).findIndex(
        device => device && device.device_name === deviceName
      );

      if (idx === -1) return prevDevices;

      const existing = prevDevices[idx];
      const hasDeviceModel = patch.device_model !== undefined && patch.device_model !== null;
      const hasAndroidVersion = patch.android_version !== undefined && patch.android_version !== null;
      const hasIp = patch.ip_address_internal !== undefined && patch.ip_address_internal !== null;
      const hasLastDisplayedFile = patch.last_displayed_file !== undefined && patch.last_displayed_file !== null && patch.last_displayed_file !== '';
      const hasWorkingHours = patch.working_hours !== undefined && patch.working_hours !== null;
      const hasStorageTotal = patch.storage_space_total !== undefined && patch.storage_space_total !== null;
      const hasStorageFree = patch.storage_space_free !== undefined && patch.storage_space_free !== null;

      const next = {
        ...existing,
        ...(hasDeviceModel ? { device_model: patch.device_model } : {}),
        ...(hasAndroidVersion ? { android_version: patch.android_version } : {}),
        ...(hasIp ? { ip_address_internal: patch.ip_address_internal } : {}),
        ...(hasLastDisplayedFile ? { last_displayed_file: patch.last_displayed_file } : {}),
        ...(hasWorkingHours ? { working_hours: patch.working_hours } : {}),
        ...(hasStorageTotal ? { storage_space_total: patch.storage_space_total } : {}),
        ...(hasStorageFree ? { storage_space_free: patch.storage_space_free } : {})
      };

      const newDevices = [...prevDevices];
      newDevices[idx] = next;
      return newDevices;
    });

    setSelectedDeviceState(prev => {
      if (!prev || prev.device_name !== deviceName) return prev;

      const hasDeviceModel = patch.device_model !== undefined && patch.device_model !== null;
      const hasAndroidVersion = patch.android_version !== undefined && patch.android_version !== null;
      const hasIp = patch.ip_address_internal !== undefined && patch.ip_address_internal !== null;
      const hasLastDisplayedFile = patch.last_displayed_file !== undefined && patch.last_displayed_file !== null && patch.last_displayed_file !== '';
      const hasWorkingHours = patch.working_hours !== undefined && patch.working_hours !== null;
      const hasStorageTotal = patch.storage_space_total !== undefined && patch.storage_space_total !== null;
      const hasStorageFree = patch.storage_space_free !== undefined && patch.storage_space_free !== null;

      return {
        ...prev,
        ...(hasDeviceModel ? { device_model: patch.device_model } : {}),
        ...(hasAndroidVersion ? { android_version: patch.android_version } : {}),
        ...(hasIp ? { ip_address_internal: patch.ip_address_internal } : {}),
        ...(hasLastDisplayedFile ? { last_displayed_file: patch.last_displayed_file } : {}),
        ...(hasWorkingHours ? { working_hours: patch.working_hours } : {}),
        ...(hasStorageTotal ? { storage_space_total: patch.storage_space_total } : {}),
        ...(hasStorageFree ? { storage_space_free: patch.storage_space_free } : {})
      };
    });
  }, []);

  const refreshDeviceInfoForOnlineDevices = useCallback(async (devicesList) => {
    const onlineDevices = (devicesList || []).filter(d => d && d.device_name && d.is_online);
    if (onlineDevices.length === 0) return;

    const maxDevices = 25;
    const targets = onlineDevices.slice(0, maxDevices);

    for (const d of targets) {
      try {
        const resp = await promediaService.getDeviceInfo(d.device_name);
        if (resp && resp.result) {
          applyDeviceInfoResultToState(d.device_name, resp.result);
        }
        await new Promise(r => setTimeout(r, 150));
      } catch (e) {
        // ignore
      }
    }
  }, [applyDeviceInfoResultToState]);

  useEffect(() => {
    if (!pendingDeviceInfoRefresh) return;
    if (!user) return;

    if (!Array.isArray(devices) || devices.length === 0) return;

    let cancelled = false;

    (async () => {
      try {
        await refreshDeviceInfoForOnlineDevices(devices);
      } finally {
        if (!cancelled) setPendingDeviceInfoRefresh(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pendingDeviceInfoRefresh, user, refreshDeviceInfoForOnlineDevices]);

  useEffect(() => {
    if (!showDeviceModal || !selectedDeviceState?.device_name) return;

    let cancelled = false;
    const deviceName = selectedDeviceState.device_name;

    (async () => {
      try {
        const resp = await promediaService.getDeviceInfo(deviceName);
        if (resp && resp.result) {
          applyDeviceInfoResultToState(deviceName, resp.result);
        }
      } catch (e) {
        if (!cancelled) {
          console.warn('Failed to refresh device info via MQTT:', e?.message || e);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showDeviceModal, selectedDeviceState?.device_name, applyDeviceInfoResultToState]);

// WebSocket connection management (DEFINED AFTER handleDeviceUpdate)
const connectWebSocket = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    try {
// Use secure WebSocket (wss) if page is loaded over HTTPS  
const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const wsEndpoints = [
  `${protocol}//${window.location.host}/api/ws`,
];

      let currentEndpointIndex = 0;
      const wsUrl = wsEndpoints[currentEndpointIndex];
      
      console.log('?? Connecting to WebSocket:', wsUrl);
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
    console.log('? WebSocket connected successfully');
    setWsConnection(ws);
    setError(null);
    
    // Expose WebSocket globally for AdminPanel
    window.wsConnection = ws;
    console.log('?? WebSocket exposed globally for AdminPanel');
    
    // Reset endpoint index on successful connection
    currentEndpointIndex = 0;
    
    // Authenticate the WebSocket connection
    const token = localStorage.getItem('token');
    if (token) {
      console.log('?? Sending WebSocket authentication');
      ws.send(JSON.stringify({
        type: 'auth',
        token: token
      }));
    }
};

      ws.onmessage = (event) => {
  try {
    const data = JSON.parse(event.data);
    
    if (data.type === 'connection_established') {
      console.log('? WebSocket connection confirmed');
    } else if (data.type === 'auth_success') {
      console.log('?? WebSocket authenticated successfully');
    } else if (data.type === 'auth_error') {
      console.error('?? WebSocket authentication failed:', data.message);
    } else if (data.type === 'device_update' && data.payload) {
      // Let handleDeviceUpdate decide whether to apply changes.
      handleDeviceUpdate(data.payload);
    } else if (data.type === 'device_updates_batch' && data.payload) {
      // BATCH PROCESSING: Limit to 5 devices max per batch
      if (Array.isArray(data.payload)) {
        const limitedUpdates = data.payload.slice(0, 5); // Limit batch size
        
        limitedUpdates.forEach(update => {
          handleDeviceUpdate(update);
        });
      }
    } else if (data.type === 'user_activity_update' && data.payload) {
      // Process user activity updates (unchanged)
      if (data.payload && data.payload.username) {
        console.log(`?? User activity updated: ${data.payload.username}`);
      }
    } else if (data.type === 'ping') {
      // Respond to server ping
      ws.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }));
    }
  } catch (err) {
    console.error('Error parsing WebSocket message:', err);
  }
};

      // Add exponential backoff for reconnection
      const getReconnectDelay = (() => {
        let attempts = 0;
        return () => {
          attempts++;
          // Exponential backoff: 5s, 10s, 20s, 40s, max 60s
          const delay = Math.min(5000 * Math.pow(2, attempts - 1), 60000);
          console.log(`?? Reconnection attempt #${attempts}, waiting ${delay/1000}s`);
          return delay;
        };
      })();

      ws.onclose = (event) => {
        console.log('? WebSocket disconnected:', event.code, event.reason);
        setWsConnection(null);
        
        // Only attempt to reconnect if user is still logged in and it wasn't a clean close
        if (user && !event.wasClean && event.code !== 1000) {
          // Try next endpoint if current one failed with 404
          if (event.code === 1006 || event.code === 1002) {
            currentEndpointIndex = (currentEndpointIndex + 1) % wsEndpoints.length;
            console.log(`?? Trying next WebSocket endpoint: ${wsEndpoints[currentEndpointIndex]}`);
          }
          
          setTimeout(() => {
            if (user) {
              console.log('?? Attempting WebSocket reconnection...');
              connectWebSocket();
            }
          }, getReconnectDelay());
        }
      };

      ws.onerror = (error) => {
        console.error('? WebSocket error:', error);
        // Don't show error message to user for WebSocket issues
        // The app works fine without real-time updates
      };

    } catch (err) {
      console.error('Error creating WebSocket connection:', err);
    }
  }, [user, handleDeviceUpdate]);

// Optimized device fetching with lazy fallback file discovery
const fetchDevicesOptimized = useCallback(async (explicitToken) => {
  const tokenToUse = explicitToken || localStorage.getItem('token');
  
  if (!tokenToUse) {
    console.error("No token available for device fetch");
    setError("Authentication error: No token available");
    setIsLoading(false);
    return;
  }
  
  try {
    console.log("Fetching devices (optimized)...");
    
    // Add timeout handling
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
    
    const response = await fetch(`${API_BASE_URL}/api/devices`, {
      headers: {
        'Authorization': `Bearer ${tokenToUse}`
      },
      credentials: 'include',
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Check for authentication errors
      if (response.status === 401 || response.status === 403) {
        handleAuthError();
        return;
      }
      throw new Error(`HTTP error! status: ${response.status}`); 
    }

    const data = await response.json();
    console.log(`Fetched ${data.length} devices successfully`);
    
    // DEBUG: Log owner_username for all devices to identify the issue
    console.log('=== DEVICE OWNERSHIP DEBUG ===');
    data.forEach(device => {
      console.log(`Device: ${device.device_name}, owner_username: "${device.owner_username}", type: ${typeof device.owner_username}`);
    });
    console.log('=== END DEVICE OWNERSHIP DEBUG ===');
    
    // Add default media links to devices that don't have them
    const devicesWithMediaLinks = data.map(device => {
      if (!device.media_files_link && device.device_name) {
        device.media_files_link = `${API_BASE_URL}/home/${device.device_name}`;
      }
      return device;
    });
    
    // Set devices by merging to avoid wiping media/thumb fields that might be missing in the API response
    setDevices(prevDevices => {
      const prevByName = new Map((prevDevices || []).filter(Boolean).map(d => [d.device_name, d]));
      return (devicesWithMediaLinks || []).map((next) => {
        const prev = prevByName.get(next.device_name);
        if (!prev) return next;

        const isEmpty = (v) => v === undefined || v === null || v === '';

        const toTs = (v) => {
          if (!v) return null;
          const t = new Date(v).getTime();
          return Number.isFinite(t) ? t : null;
        };

        const prevLastSeenTs = toTs(prev.last_seen);
        const nextLastSeenTs = toTs(next.last_seen);

        // Avoid UI flapping: if we already know the device is online, don't let a refresh
        // downgrade it to offline unless the refresh provides a newer last_seen.
        const shouldKeepPrevOnline =
          prev.is_online === true &&
          next.is_online === false &&
          (nextLastSeenTs === null || (prevLastSeenTs !== null && nextLastSeenTs < prevLastSeenTs));

        const onlineMergePatch = shouldKeepPrevOnline
          ? { is_online: prev.is_online, last_seen: prev.last_seen }
          : {};

        return {
          ...prev,
          ...next,
          ...onlineMergePatch,
          ...(isEmpty(next.last_displayed_file) && !isEmpty(prev.last_displayed_file)
            ? { last_displayed_file: prev.last_displayed_file, last_displayed_file_time: prev.last_displayed_file_time }
            : {}),
          ...(isEmpty(next.fallback_file) && !isEmpty(prev.fallback_file)
            ? {
                fallback_file: prev.fallback_file,
                fallback_file_path: prev.fallback_file_path,
                fallback_file_modified: prev.fallback_file_modified,
                fallback_file_is_video: prev.fallback_file_is_video,
                fallback_files_total: prev.fallback_files_total
              }
            : {})
        };
      });
    });

    // Lazy load fallback media in the background for devices that need it
    const devicesNeedingFallback = devicesWithMediaLinks.filter(
      device => !device.last_displayed_file && !device.fallback_file
    );

    if (false && devicesNeedingFallback.length > 0) {
      console.log(`Lazy loading fallback media for ${devicesNeedingFallback.length} devices`);
      
      // Process fallback media in small batches to avoid overwhelming the server
      const batchSize = 1; // Process one at a time to reduce bandwidth
      const maxConcurrent = 3; // Maximum 3 simultaneous requests
      const batches = [];
      for (let i = 0; i < devicesNeedingFallback.length; i += batchSize) {
        batches.push(devicesNeedingFallback.slice(i, i + batchSize));
      }
      
      // Process batches with delay between them
      const processBatch = async (batch, batchIndex) => {
        if (batch.length === 0) return; // Early return for empty batches
        
        try {
          const fallbackPromises = batch.map(async (device) => {
            try {
              const fallbackController = new AbortController();
              const fallbackTimeout = setTimeout(() => fallbackController.abort(), 3000); // 3 second timeout
              
              const fallbackResponse = await fetch(
                `${API_BASE_URL}/api/devices/${device.device_name}/fallback-media`,
                {
                  headers: { 'Authorization': `Bearer ${tokenToUse}` },
                  credentials: 'include',
                  signal: fallbackController.signal
                }
              );
              
              clearTimeout(fallbackTimeout);
              
              if (fallbackResponse.ok) {
                const fallbackData = await fallbackResponse.json();
                console.log(`Found fallback media for ${device.device_name}:`, fallbackData.filename);
                return {
                  deviceName: device.device_name,
                  fallbackData
                };
              }
            } catch (error) {
              if (error.name !== 'AbortError') {
                console.warn(`Error fetching fallback media for ${device.device_name}:`, error);
              }
            }
            return null;
          });
          
          const batchResults = await Promise.all(fallbackPromises);
          const validResults = batchResults.filter(result => result !== null);
          
          if (validResults.length > 0) {
            // Update devices state with fallback data
            setDevices(prevDevices => 
              prevDevices.map(device => {
                const fallbackResult = validResults.find(r => r.deviceName === device.device_name);
                if (fallbackResult) {
                  return {
                    ...device,
                    fallback_file: fallbackResult.fallbackData.filename,
                    fallback_file_path: fallbackResult.fallbackData.relativePath,
                    fallback_file_modified: fallbackResult.fallbackData.modified,
                    fallback_file_is_video: fallbackResult.fallbackData.isVideo,
                    fallback_files_total: fallbackResult.fallbackData.totalFilesFound
                  };
                }
                return device;
              })
            );
          }
          
          // Add delay between batches to avoid server overload
          if (batchIndex < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500)); // Reduced delay
          }
        } catch (error) {
          console.error(`Error processing fallback batch ${batchIndex}:`, error);
        }
      };
      
      // Process all batches sequentially
      for (let i = 0; i < batches.length; i++) {
        processBatch(batches[i], i);
      }
    }

    setError(null);
  } catch (err) {
    console.error("Fetch error:", err);
    
    // Check if error is due to expired/invalid token
    if (err.message.includes('401') || err.message.includes('403') || 
        err.message.includes('token') || err.message.includes('Unauthorized')) {
      handleAuthError();
      return;
    }
    
    // Better error messages
    if (err.name === 'AbortError') {
      setError('Request timed out. Please try again.');
    } else if (err.message.includes('Failed to fetch')) {
      setError('Cannot connect to server. Check your connection.');
    } else {
      setError(`Failed to load devices: ${err.message}`);
    }
    
    setDevices([]);
  } finally {
    setIsLoading(false);
  }
}, [handleAuthError]);

  // Memoized helper functions
  const getMediaType = useCallback((filename) => {
  if (!filename) return 'unknown';
  
  const extension = filename.split('.').pop().toLowerCase();
  
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif'].includes(extension)) {
    return 'image';
  }
  
  if (['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'm4v', 'mpg', 'mpeg'].includes(extension)) {
    return 'video';
  }
  
  if (extension === 'pdf') {
    return 'pdf';
  }
  
  return 'unknown';
}, []);

  const truncateFilename = useCallback((filename, maxLength) => {
    if (!filename) return '';
    
    const lastDotIndex = filename.lastIndexOf('.');
    const extension = lastDotIndex !== -1 ? filename.substring(lastDotIndex) : '';
    const baseName = lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;
    
    if (baseName.length <= maxLength) {
      return filename;
    }
    
    return baseName.substring(0, maxLength) + '...' + extension;
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (e) {
      console.error("Error formatting date:", dateString, e);
      return 'Format Error';
    }
  }, []);

  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
      const formattedTime = date.toLocaleTimeString('en-US', timeOptions);
      
      return `${day}/${month}/${year}, ${formattedTime}`;
    } catch (e) {
      console.error("Error formatting date time:", dateString, e);
      return 'Format Error';
    }
  }, []);

  const formatMediaTime = useCallback((timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      const timeOptions = { hour: '2-digit', minute: '2-digit', hour12: true };
      return `@ ${date.toLocaleTimeString('en-US', timeOptions)}`;
    } catch (e) { 
      console.error("Error formatting media time:", timestamp, e); 
      return ''; 
    }
  }, []);

  const calculateStoragePercentage = useCallback((free, total) => {
    if (!free || !total || isNaN(Number(free)) || isNaN(Number(total))) return 0;
    const freeNum = Number(free);
    const totalNum = Number(total);
    if (totalNum === 0) return 0;
    const usedPercentage = ((totalNum - freeNum) / totalNum) * 100;
    return Math.min(Math.max(usedPercentage, 0), 100);
  }, []);

  const formatBytes = useCallback((bytes, decimals = 1) => {
    if (bytes == null || isNaN(Number(bytes))) return 'N/A';
    const numBytes = Number(bytes); 
    if (numBytes === 0) return '0 Bytes';
    const k = 1024; 
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(numBytes) / Math.log(k));
    const unitIndex = i < sizes.length ? i : sizes.length - 1;
    return parseFloat((numBytes / Math.pow(k, unitIndex)).toFixed(dm)) + ' ' + sizes[unitIndex];
  }, []);

// Handle thumbnail preview click - FIXED VERSION
const handleThumbnailClick = useCallback((device) => {
  console.log('??? Thumbnail clicked for device:', device.device_name);

  const fileToDisplay = device.last_displayed_file || device.fallback_file;
  const owner = device.owner_username;
  const deviceName = device.device_name;

  if (!fileToDisplay || !owner || !deviceName) {
    console.warn("? Missing required data for preview:", { fileToDisplay, owner, deviceName });
    alert("Preview not available: Missing file or device information.");
    return;
  }

  const mediaType = getMediaType(fileToDisplay);
  let previewUrl;

  if (mediaType === 'pdf') {
    // For PDFs, use backend preview proxy to avoid browser basic-auth prompts
    const token = localStorage.getItem('token') || '';
    const webdavPath = `/home/${owner}/${deviceName}/${fileToDisplay}`;
    previewUrl = `${API_BASE_URL}/api/webdav/preview?path=${encodeURIComponent(webdavPath)}&token=${encodeURIComponent(token)}`;
    console.log('?? PDF Preview URL (Backend Proxy):', previewUrl);
  } else {
    // For images/videos, use the thumbnail generation API
    previewUrl = `${API_BASE_URL}/api/thumbnail/${encodeURIComponent(deviceName)}/${encodeURIComponent(fileToDisplay)}`;
    console.log('?? Image/Video Preview API URL:', previewUrl);
  }

  setPreviewItem({
    type: mediaType,
    url: previewUrl,
    pathForFetch: `/home/${owner}/${deviceName}/${fileToDisplay}`, // Keep for potential downloads
    filename: fileToDisplay,
    deviceName: deviceName
  });

  setShowPreviewModal(true);
  console.log('? Preview modal opened');
}, [getMediaType]);
  
  // Handle user player click (for file manager)
  const handleUserPlayerClick = useCallback((device) => {
  console.log("Row clicked for device:", device.device_name, "Opening modal file manager.");
  if (deviceForFileManagerModal && deviceForFileManagerModal.device_name === device.device_name) {
    // If clicking the same device row that's already open in modal, close it.
    setDeviceForFileManagerModal(null);
  } else {
    setDeviceForFileManagerModal(device);
  }
}, [deviceForFileManagerModal]);

// Auto-logout after 30 minutes of inactivity
useEffect(() => {
  // Only run if user is logged in
  if (!user) return;
  
  const isAdmin = user?.is_admin === true || user?.role === 'admin';
  const timeoutMinutes = isAdmin ? 60 : 15; // 60 min for admin, 15 min for users

  console.log(`?? Inactivity monitor started - ${timeoutMinutes} minute timeout`);
  
  // Update last activity time on user interactions
  const updateActivity = () => {
    lastActivityRef.current = Date.now();
  };
  
  // Listen to various user activities
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  events.forEach(event => {
    document.addEventListener(event, updateActivity);
  });
  
  // Check for inactivity every minute
  const inactivityCheckInterval = setInterval(() => {
    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    const inactivityTimeout = timeoutMinutes * 60 * 1000;
    
    // Debug log (you can remove this later)
    const minutesInactive = Math.floor(timeSinceLastActivity / 60000);
    console.log(`?? Inactivity check: ${minutesInactive} minutes since last activity`);
    
    // Auto-logout if timeout passed
    if (timeSinceLastActivity >= inactivityTimeout) {
      console.log(`?? ${timeoutMinutes} minutes of inactivity detected - auto logging out`);
      
      // Perform logout directly
      setUser(null);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      
      // Close WebSocket
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (window.wsConnection) {
        window.wsConnection.close();
        delete window.wsConnection;
      }
      
      // Call logout API
      fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      }).catch(err => console.error('Logout error:', err));
    }
  }, 60000); // Check every 1 minute
  
  // Cleanup on unmount or user logout
  return () => {
    events.forEach(event => {
      document.removeEventListener(event, updateActivity);
    });
    clearInterval(inactivityCheckInterval);
    console.log('?? Inactivity monitor stopped');
  };
}, [user]); // Only depend on 'user', not handleLogout

// Check if user is logged in from localStorage on component mount
useEffect(() => {
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const parsed = JSON.parse(storedUser);
      setUser(parsed);
      setPendingDeviceInfoRefresh(true);

      // Background refresh to pick up any new fields (e.g. brand_logo) without requiring re-login
      const token = localStorage.getItem('token');
      if (token) {
        fetch('/api/auth/profile', {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include'
        })
          .then(r => r.ok ? r.json() : null)
          .then(profile => {
            if (profile) {
              const updated = { ...parsed, ...profile };
              setUser(updated);
              localStorage.setItem('user', JSON.stringify(updated));
            }
          })
          .catch(() => {}); // silent — we already have stored data
      }
    } catch (e) {
      console.error('Error parsing stored user:', e);
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    }
  }

  // Handle browser refresh and back button
  const handleBeforeUnload = (e) => {
    // Store current state before refresh
    localStorage.setItem('appState', JSON.stringify({
      showAdminPanel,
      showFileManager,
      showDeviceModal,
      isEditingDevice,
      isAddingDevice
    }));
  };

  const handlePopState = (e) => {
    // Handle back button on mobile - prevent exit
    if (showDeviceModal || showAdminPanel || showFileManager || showGroupManager) {
      e.preventDefault();
      // Close modals instead of going back
      if (showDeviceModal) {
        setShowDeviceModal(false);
        setIsEditingDevice(false);
        setIsAddingDevice(false);
        setSelectedDeviceState(null);
      } else if (showAdminPanel) {
        setShowAdminPanel(false);
      } else if (showFileManager) {
        setShowFileManager(false);
      } else if (showGroupManager) {
        setShowGroupManager(false);
      }
      // Push state back to prevent actual navigation
      window.history.pushState(null, '', window.location.href);
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener('popstate', handlePopState);

  // Push initial state to handle back button
  window.history.pushState(null, '', window.location.href);

  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.removeEventListener('popstate', handlePopState);
  };
}, [showAdminPanel, showFileManager, showGroupManager, showDeviceModal, isEditingDevice, isAddingDevice]);

// Refresh the logged-in user's profile from API (e.g. after admin edits own account)
const refreshCurrentUser = useCallback(async () => {
  const token = localStorage.getItem('token');
  if (!token) return;
  try {
    const r = await fetch('/api/auth/profile', {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include'
    });
    if (!r.ok) return;
    const profile = await r.json();
    const current = JSON.parse(localStorage.getItem('user') || '{}');
    const updated = { ...current, ...profile };
    setUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
  } catch (_) {}
}, []);

useEffect(() => {
  const handler = () => refreshCurrentUser();
  window.addEventListener('pm:refresh-user', handler);
  return () => window.removeEventListener('pm:refresh-user', handler);
}, [refreshCurrentUser]);

// Fetch all users for owner dropdown (not just device owners)
const refreshOwners = useCallback(async () => {
  const token = localStorage.getItem('token');
  if (!token || !user || !user.is_admin) { setUniqueOwners([]); return; }
  if (ownersFetchStateRef.current.inFlight) return;
  ownersFetchStateRef.current.inFlight = true;
  ownersFetchStateRef.current.lastFetchAt = Date.now();
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
      headers: { 'Authorization': `Bearer ${token}` },
      credentials: 'include',
    });
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) { handleAuthError(); return; }
      throw new Error(`HTTP ${response.status}`);
    }
    const allUsers = await response.json();
    // Filter to OWNERS only — managers/operators don't own devices.
    // Their devices are reached via their parent owner's device list.
    const owners = allUsers.filter(u => (u.role || (u.is_admin ? 'admin' : 'owner')) === 'owner');
    const usernames = owners.map(u => u.username).filter(Boolean).sort();
    setUniqueOwners(usernames);
    const logoMap = {};
    owners.forEach(u => { if (u.username && u.brand_logo) logoMap[u.username] = u.brand_logo; });
    setOwnerLogoMap(logoMap);
  } catch (error) {
    console.error('Failed to fetch owners:', error);
    if (devices.length > 0) {
      const owners = [...new Set(devices.map(d => d.owner_username).filter(Boolean))].sort();
      setUniqueOwners(owners);
    }
  } finally {
    ownersFetchStateRef.current.inFlight = false;
  }
}, [user, devices]); // eslint-disable-line

useEffect(() => {
  if (user && user.is_admin) refreshOwners();

  // 2026-05-19 Phase C — fetch the brand catalog once (admin scope).
  // Each brand carries owner_username so we can filter client-side as the
  // admin picks an owner in the Add/Edit Device modal.
  (async () => {
    if (!user || !user.is_admin) { setAllBrands([]); return; }
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_BASE_URL}/api/m/brands`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (r.ok) {
        const list = await r.json();
        setAllBrands(Array.isArray(list) ? list : []);
      }
    } catch (e) { console.warn('Failed to fetch brands:', e); }
  })();
}, [user]); // eslint-disable-line

// 2026-05-19 Phase C — derived owner→brand cascade. availableBrands is the
// subset of allBrands owned by selectedOwner. When the owner has exactly one
// brand, we auto-pin it on deviceFormData so admin doesn't have to click.
const availableBrands = React.useMemo(() => {
  if (!selectedOwner) return [];
  return (allBrands || []).filter(b => b.owner_username === selectedOwner);
}, [allBrands, selectedOwner]);

// Auto-pick the single brand for an owner. Only fires when no brand is set
// yet (so admin's manual choice isn't clobbered).
useEffect(() => {
  if (!selectedOwner) return;
  if (deviceFormData.brand_id) return;
  if (availableBrands.length === 1) {
    setDeviceFormData(prev => ({ ...prev, brand_id: availableBrands[0].id }));
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [availableBrands, selectedOwner]);

// 2026-05-19 Phase C — fetch branches whenever the selected brand changes.
// Uses /api/m/branches?brand_id=X. Auto-pin the single branch when only one
// exists. Clears the branch dropdown if no brand is selected.
// The endpoint already scopes to brand_id and brand_id is itself scoped to
// the selected owner (availableBrands filters by owner_username), so every
// row returned here is already an owner-registered branch.
useEffect(() => {
  if (!deviceFormData.brand_id) { setAvailableBranches([]); return; }
  let cancelled = false;
  (async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_BASE_URL}/api/m/branches?brand_id=${deviceFormData.brand_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!r.ok || cancelled) return;
      const list = await r.json();
      if (cancelled) return;
      const branches = Array.isArray(list) ? list : [];
      setAvailableBranches(branches);
      // Auto-pin if exactly one branch AND no branch selected yet.
      if (branches.length === 1 && !deviceFormData.branch_id) {
        setDeviceFormData(prev => ({ ...prev, branch_id: branches[0].id }));
      }
    } catch (e) { if (!cancelled) console.warn('Failed to fetch branches:', e); }
  })();
  return () => { cancelled = true; };
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [deviceFormData.brand_id]);

// 2026-05-20 — Auto-derive the WebDAV path WITHOUT the owner segment.
//   Stored = /<branch_folder>/<device_name>      (e.g. /banha/banha-2)
//   Visible = same
// Rationale: the WebDAV server's path-permission resolver looks up the
// owner from `client_device_map` and prepends it to the disk path itself
// (HOMES_ROOT/<owner>/<rest>). If the URL also contained <owner>, the disk
// path would be doubled (.../<owner>/<owner>/...) AND the SQL LIKE match
// against media_files_link would refuse any request that lacked the owner
// segment. Old devices (e.g. banha-2 = /home/banha/banha-2) work because
// they followed this no-owner convention; new devices that added <owner>
// were broken (the user's "check-player-1" 403 test case).
useEffect(() => {
  if (mediaFolderTouched) return;
  if (!deviceFormData.device_name) return;
  const branch = availableBranches.find(br => br.id === parseInt(deviceFormData.branch_id, 10));
  const branchFolder = branch?.folder_name || branch?.name || '';
  const slug = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
  const segments = [];
  if (branchFolder)  segments.push(slug(branchFolder));
  segments.push(deviceFormData.device_name);
  const path = '/' + segments.join('/');
  setDeviceFormData(prev => {
    const next = `${API_BASE_URL}/home${path}`;
    if (prev.media_files_link === next) return prev;
    return { ...prev, media_files_link: next };
  });
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [deviceFormData.device_name, deviceFormData.branch_id, availableBranches, mediaFolderTouched]);

// Helpers shared by both Add + Edit modals. Display value strips the
// /<owner-slug>/ prefix so the input shows only the branch-relative path
// the admin can edit. onChange re-attaches it before storing.
const ownerPathSlug = React.useMemo(() => {
  if (!selectedOwner) return '';
  return String(selectedOwner).toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '');
}, [selectedOwner]);

const stripOwnerFromPath = useCallback((fullLink) => {
  if (!fullLink) return '';
  const baseUrl = `${API_BASE_URL}/home`;
  let path = '';
  if (fullLink.startsWith(baseUrl)) {
    path = fullLink.substring(baseUrl.length) || '/';
  } else {
    path = fullLink.replace(/^https?:\/\/[^/]+\/home/, '') || '/';
  }
  if (ownerPathSlug && path.startsWith(`/${ownerPathSlug}/`)) {
    return path.substring(ownerPathSlug.length + 1); // drop "/owner"
  }
  if (ownerPathSlug && path === `/${ownerPathSlug}`) {
    return '/';
  }
  return path;
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [ownerPathSlug]);

const applyOwnerToPath = useCallback((displayedPath) => {
  // 2026-05-20 — Path no longer carries the owner segment (see auto-derive
  // comment above). The displayed path == stored path. Helper kept for
  // backwards-compat callsites; no longer prepends ownerPathSlug.
  let p = (displayedPath || '').trim();
  if (!p.startsWith('/')) p = '/' + p;
  if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1);
  return `${API_BASE_URL}/home${p}`;
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

// Re-enable auto-derive when the org cascade (owner / brand / branch /
// device_name) changes. Without this, an admin who first types a custom
// path then later picks a different branch would keep seeing the old custom
// path — making the new branch invisible in the field. Cascade change is a
// strong signal that the previous auto-derive is no longer relevant.
useEffect(() => {
  setMediaFolderTouched(false);
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedOwner, deviceFormData.brand_id, deviceFormData.branch_id, deviceFormData.device_name]);

// Reset modal-only state whenever the device modal opens. The cascade-
// change effect below resets mediaFolderTouched on its own whenever
// owner/brand/branch/device_name flips, so we don't need a redundant reset
// here for the media-folder field.
useEffect(() => {
  if (showDeviceModal) {
    setAddingBranch(false);
    setNewBranchName('');
  }
}, [showDeviceModal]);

// 2026-05-19 Phase C — "+ Add new branch" inline. POSTs to /api/m/branches
// with the current brand_id, then auto-selects the new branch on success.
const handleCreateBranchInline = useCallback(async () => {
  const trimmed = (newBranchName || '').trim();
  if (!trimmed || !deviceFormData.brand_id) return;
  try {
    const token = localStorage.getItem('token');
    const r = await fetch(`${API_BASE_URL}/api/m/branches`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify({ brand_id: parseInt(deviceFormData.brand_id, 10), name: trimmed }),
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      alert(`Failed to add branch: ${err.error || r.status}`);
      return;
    }
    const created = await r.json();
    setAvailableBranches(prev => [...prev, created]);
    setDeviceFormData(prev => ({ ...prev, branch_id: created.id }));
    setAddingBranch(false);
    setNewBranchName('');
  } catch (e) {
    alert(`Failed to add branch: ${e.message}`);
  }
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [newBranchName, deviceFormData.brand_id]);

  // Handle clicking outside the actions menu
useEffect(() => {
  function handleClickOutside(event) {
    if (showActionsMenu &&
        !event.target.closest('.device-card-header') &&
        !event.target.closest('.actions-menu-dropdown')) {
      setShowActionsMenu(null);
    }
  }

  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [showActionsMenu]);

// Players table 3-line menu: close on outside click + scroll.
useEffect(() => {
  function onMouseDown(e) {
    if (!openPlayerRowMenuId) return;
    if (e.target.closest('.ply-row-menu')) return;
    if (e.target.closest('.ply-row-kebab')) return;
    if (e.target.closest('.ply-row')) return; // row click reassigns; don't pre-close
    closePlayerRowMenu();
  }
  function onScroll() { if (openPlayerRowMenuId) closePlayerRowMenu(); }
  document.addEventListener('mousedown', onMouseDown);
  window.addEventListener('scroll', onScroll, true);
  return () => {
    document.removeEventListener('mousedown', onMouseDown);
    window.removeEventListener('scroll', onScroll, true);
  };
}, [openPlayerRowMenuId, closePlayerRowMenu]);

// Position the actions menu after it appears
useEffect(() => {
  if (showActionsMenu && window.menuPosition) {
    const dropdown = document.querySelector('.actions-menu-dropdown');
    if (dropdown) {
      const { top, left } = window.menuPosition;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let finalTop = top;
      let finalLeft = left;
      
      const dropdownWidth = dropdown.offsetWidth;
      const dropdownHeight = dropdown.offsetHeight;
      
      // Adjust if menu would go off screen
      if (finalLeft + dropdownWidth > viewportWidth - 10) {
        finalLeft = Math.max(10, viewportWidth - dropdownWidth - 10);
      }
      
      if (finalTop + dropdownHeight > viewportHeight - 10) {
        finalTop = finalTop - dropdownHeight - 10;
      }
      
      dropdown.style.top = `${finalTop}px`;
      dropdown.style.left = `${finalLeft}px`;
      
      console.log('?? Menu positioned at:', finalTop, finalLeft);
    }
  }
}, [showActionsMenu]);

// Cleanup tasks - OPTIMIZED for thumbnail persistence
  useEffect(() => {
    // Smart memory management that preserves thumbnails
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      
      // 1. Clean device update throttling map (only very old entries)
      const deviceCutoff = now - (15 * 60 * 1000); // 15 minutes ago
      for (const [deviceName, timestamp] of deviceUpdateTimes.current.entries()) {
        if (timestamp < deviceCutoff) {
          deviceUpdateTimes.current.delete(deviceName);
        }
      }
      
      // 2. Clean in-memory thumbnail cache GENTLY (keep most recent)
      if (thumbnailCache.current.size > 100) { // Increased threshold
        // Only remove oldest 30 entries, keep the rest
        const entries = Array.from(thumbnailCache.current.entries())
          .sort((a, b) => a[1].timestamp - b[1].timestamp); // Sort by age
        
        const toRemove = entries.slice(0, 30); // Remove oldest 30
        toRemove.forEach(([key]) => {
          thumbnailCache.current.delete(key);
        });
        
        console.log(`?? Removed ${toRemove.length} oldest thumbnail cache entries (${thumbnailCache.current.size} remaining)`);
      }
      
      // 3. Clean localStorage thumbnails ONLY if corrupted or excessive
      try {
        const keys = Object.keys(localStorage);
        const thumbKeys = keys.filter(key => key.startsWith('thumb_'));
        
        // Only remove corrupted entries (can't parse) or very old (30+ days)
        const veryOldCutoff = now - (30 * 24 * 60 * 60 * 1000); // 30 days
        let removedCorrupted = 0;
        let removedOld = 0;
        
        thumbKeys.forEach(key => {
          try {
            const cached = JSON.parse(localStorage.getItem(key));
            
            // Remove if data is corrupted or very old
            if (!cached || !cached.timestamp || cached.timestamp < veryOldCutoff) {
              localStorage.removeItem(key);
              if (!cached || !cached.timestamp) {
                removedCorrupted++;
              } else {
                removedOld++;
              }
            }
          } catch (e) {
            // Corrupted entry, remove it
            localStorage.removeItem(key);
            removedCorrupted++;
          }
        });
        
        if (removedCorrupted > 0 || removedOld > 0) {
          console.log(`?? Cleaned localStorage: ${removedCorrupted} corrupted, ${removedOld} very old (30+ days)`);
        }
        
        // Only if localStorage is REALLY excessive (200+ thumbnails), keep newest 100
        if (thumbKeys.length > 200) {
          const thumbData = [];
          
          thumbKeys.forEach(key => {
            try {
              const cached = JSON.parse(localStorage.getItem(key));
              if (cached && cached.timestamp) {
                thumbData.push({ key, timestamp: cached.timestamp });
              }
            } catch (e) {
              // Already removed above
            }
          });
          
          // Sort by timestamp (newest first)
          thumbData.sort((a, b) => b.timestamp - a.timestamp);
          
          // Remove all except newest 100
          const toRemove = thumbData.slice(100);
          toRemove.forEach(item => localStorage.removeItem(item.key));
          
          console.log(`?? Reduced localStorage thumbnails from ${thumbKeys.length} to 100`);
        }
        
      } catch (e) {
        console.warn('?? Storage cleanup error:', e);
      }
      
      // 4. Garbage collection (very rarely, don't force it)
      if (global.gc && Math.random() < 0.02) { // Only 2% chance
        try {
          global.gc();
          console.log('??? Triggered garbage collection');
        } catch (e) {
          // GC not available, ignore
        }
      }
      
    }, 10 * 60 * 1000); // Every 10 minutes (less frequent)
    
    return () => {
      clearInterval(cleanupInterval);
    };
  }, []); // Main effect for user login and WebSocket setup with centralized activity tracking
useEffect(() => {
  if (user) {
    setIsLoading(true);
    
    // Initial device fetch
    fetchDevicesOptimized();
    fetchEmptyDevicesCount();
    fetchSystemInfo();
    
    // Setup WebSocket connection for real-time updates
    connectWebSocket();
    
    // CENTRALIZED USER ACTIVITY TRACKING - This will track activity across ALL components
const updateUserActivity = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/admin/users/update-last-login`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
    
    if (response.ok) {
      const data = await response.json();
      console.log(`?? User activity updated for: ${data.username}`);
      
      // Store the last activity time locally for immediate UI updates
      localStorage.setItem('lastActivity', new Date().toISOString());
      
      // Expose this function globally so AdminPanel can use it
      window.updateUserActivity = updateUserActivity;
      window.refreshOwners = refreshOwners;
      
    } else {
      console.error('?? Failed to update user activity:', response.status);
    }
    
  } catch (err) {
    console.error('?? Error updating user activity:', err);
  }
};

    // Update immediately when component mounts
    updateUserActivity();
    
    // Update every 30 seconds while user is active on ANY part of the app
    const activityInterval = setInterval(updateUserActivity, 30000);
    
    // Track user interactions across the entire app (not just AdminPanel)
    const handleUserActivity = () => {
      updateUserActivity();
    };
    
    // Listen for user activity events across the entire document
    document.addEventListener('mousedown', handleUserActivity);
    document.addEventListener('keydown', handleUserActivity);
    document.addEventListener('scroll', handleUserActivity);
    document.addEventListener('click', handleUserActivity);
    
    // Cleanup function
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      clearInterval(activityInterval);
      document.removeEventListener('mousedown', handleUserActivity);
      document.removeEventListener('keydown', handleUserActivity);
      document.removeEventListener('scroll', handleUserActivity);
      document.removeEventListener('click', handleUserActivity);
    };
  } else {
    // User logged out, cleanup WebSocket
    if (wsRef.current) {
      wsRef.current.close();
    }
    setWsConnection(null);
  }
}, [user, fetchDevicesOptimized, connectWebSocket, fetchSystemInfo]);

// Auto-refresh system info every 30 seconds for admins
useEffect(() => {
  if (user && user.is_admin) {
    const systemInfoInterval = setInterval(() => {
      fetchSystemInfo();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(systemInfoInterval);
  }
}, [user, fetchSystemInfo]);

  // Memoized computed values
  const deviceCounts = useMemo(() => {
  // Determine the set of devices relevant to the current user
  let relevantDevices;
  if (user && user.is_admin) {
    relevantDevices = devices; // Admin sees all devices
  } else if (user && user.username) {
    // Non-admin: filter by own username (owner) OR parent_username (staff
    // proxy through their owner). Staff users see their parent owner's devices,
    // since managers don't directly own anything.
    const ownNameLower    = (user.username || '').toLowerCase().trim();
    const parentNameLower = (user.parent_username || '').toLowerCase().trim();
    const candidateNames  = new Set([ownNameLower, parentNameLower].filter(Boolean));

    relevantDevices = devices.filter(device => {
      const ownerUsername = (device.owner_username || '').toLowerCase().trim();
      const matches = candidateNames.has(ownerUsername);
      if (!matches && device.device_name) {
        console.log(`Device ${device.device_name} filtered out: owner="${device.owner_username}" vs user="${user.username}" parent="${user.parent_username || ''}"`);
      }
      return matches;
    });

    console.log(`User ${user.username}: ${devices.length} total, ${relevantDevices.length} visible`);
  } else {
    relevantDevices = []; // Should not happen if user is logged in, or devices haven't loaded
  }

  const online = relevantDevices.filter(device => device.is_online).length;
  const offline = relevantDevices.filter(device => !device.is_online).length;
  const total = relevantDevices.length;

  return { 
      online, 
      offline, 
      total 
  };
}, [devices, user]);

// Restore app state after refresh
useEffect(() => {
  if (user) {
    const storedAppState = localStorage.getItem('appState');
    if (storedAppState) {
      try {
        const appState = JSON.parse(storedAppState);
        if (appState.showAdminPanel) setShowAdminPanel(true);
        if (appState.showFileManager) setShowFileManager(true);
        if (appState.showDeviceModal) setShowDeviceModal(true);
        if (appState.isEditingDevice) setIsEditingDevice(true);
        if (appState.isAddingDevice) setIsAddingDevice(true);
        // Clear stored state after restoration
        localStorage.removeItem('appState');
      } catch (e) {
        console.error('Error restoring app state:', e);
        localStorage.removeItem('appState');
      }
    }
  }
}, [user]);

// Helper function to extract parent folder (branch) name from media_files_link
const extractBranchName = useCallback((device) => {
  // branch_id is the real answer and now arrives resolved on the device row.
  // Parsing it out of the media-folder URL was always a guess: it breaks when
  // a device is moved between branches without its folder being renamed.
  if (device.branch_name) return device.branch_name.toLowerCase();
  if (!device.media_files_link) return 'zzz_no_folder'; // Sort devices without links last
  
  try {
    const url = new URL(device.media_files_link);
    const path = url.pathname.replace('/home/', '').replace(/\/$/, '');
    const parts = path.split('/').filter(part => part); // Remove empty parts
    
    // For path like "/user/branch/device", we want "branch" (second-to-last part)
    if (parts.length >= 2) {
      return parts[parts.length - 2].toLowerCase(); // Parent folder
    } else if (parts.length === 1) {
      return 'aaa_main'; // Devices in main folder sort first
    }
    return 'zzz_no_folder';
  } catch (e) {
    return 'zzz_no_folder';
  }
}, []);

// Brands present in the current device list, for the admin filter. Derived
// from the devices rather than from the users table, so it lists exactly what
// is on screen — and an owner holding two brands contributes both.
const uniqueBrands = useMemo(() => {
  const names = new Set();
  (devices || []).forEach(d => { if (d.brand_name) names.add(d.brand_name); });
  return [...names].sort((a, b) => a.localeCompare(b));
}, [devices]);

const filteredDevices = useMemo(() => {
  // Early return for empty devices
  if (!devices || devices.length === 0) {
    return [];
  }

  // Determine user scope once
  const isUserAdmin = user?.is_admin;
  const currentUsername = user?.username;
  
  let initialScopedDevices;
  if (!isUserAdmin && currentUsername) {
    // For non-admin users, filter to owned devices only
    // Non-admin: own username OR parent_username (staff proxy through owner).
    const ownNameLower    = (currentUsername || '').toLowerCase().trim();
    const parentNameLower = (user?.parent_username || '').toLowerCase().trim();
    const candidateNames  = new Set([ownNameLower, parentNameLower].filter(Boolean));

    initialScopedDevices = devices.filter(device => {
      const ownerUsername = (device.owner_username || '').toLowerCase().trim();
      const matches = candidateNames.has(ownerUsername);
      if (!matches && device.device_name) {
        console.log(`Device ${device.device_name} filtered out: owner="${device.owner_username}" vs user="${user?.username}" parent="${user?.parent_username || ''}"`);
      }
      return matches;
    });
    
    console.log(`User ${user.username}: ${devices.length} total devices, ${initialScopedDevices.length} visible (${devices.length - initialScopedDevices.length} filtered out)`);
  } else {
    // For admin users, show all devices
    initialScopedDevices = devices;
  }

  // Pre-compute filter conditions for better performance
  const hasTextFilter = filterText && filterText.trim().length > 0;
  const hasOwnerFilter = isUserAdmin && ownerFilter && ownerFilter.trim().length > 0;
  const hasStatusFilter = statusFilter !== 'all';
  
  const lowerCaseFilter = hasTextFilter ? filterText.toLowerCase().trim() : '';
  
  // Apply filters in order of selectivity (most selective first)
  let filtered = initialScopedDevices;

  // 1. Status filter (usually most selective)
  if (hasStatusFilter) {
    if (statusFilter === 'online') {
      filtered = filtered.filter(device => device.is_online === true);
    } else if (statusFilter === 'offline') {
      filtered = filtered.filter(device => device.is_online === false);
    }
  }

  // 2. Owner filter (admin only, moderately selective)
  if (hasOwnerFilter) {
    if (ownerFilter === 'no-owner') {
      // Must match the chip's own predicate exactly, or the number and the
      // list measure different things.
      filtered = filtered.filter(device => !device.brand_id);
    } else {
      filtered = filtered.filter(device => device.brand_name === ownerFilter);
    }
  }

  // 3. Unified text filter — applies to the field admin picked in the search
  //    dropdown (name / uid / description / register_date). Substring match.
  if (hasTextFilter) {
    filtered = filtered.filter(device => {
      let target;
      switch (searchField) {
        case 'uid':           target = device.device_uid;       break;
        case 'description':   target = device.description;      break;
        case 'register_date': target = device.register_date;    break;
        case 'name':
        default:              target = device.device_name;      break;
      }
      return target && String(target).toLowerCase().includes(lowerCaseFilter);
    });
  }

  // 5. SORT: Admin by owner then device name, Users by device name only
  if (isUserAdmin) {
    // Admin: Sort by brand first, then branch, then device name
    filtered.sort((a, b) => {
      // 1. Compare owner first
      const ownerA = (a.brand_name || 'zzz_no_brand').toLowerCase();
      const ownerB = (b.brand_name || 'zzz_no_brand').toLowerCase();
      
      if (ownerA !== ownerB) {
        return ownerA.localeCompare(ownerB);
      }
      
      // 2. Same owner, compare branch/folder
      const branchA = extractBranchName(a);
      const branchB = extractBranchName(b);
      
      if (branchA !== branchB) {
        return branchA.localeCompare(branchB);
      }
      
      // 3. Same owner and branch, sort by device name
      const nameA = (a.device_name || '').toLowerCase();
      const nameB = (b.device_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  } else {
    // Users: Sort by branch/folder name first, then device name
    filtered.sort((a, b) => {
      const branchA = extractBranchName(a);
      const branchB = extractBranchName(b);
      
      // Compare branch names first
      if (branchA !== branchB) {
        return branchA.localeCompare(branchB);
      }
      
      // Same branch, sort by device name
      const nameA = (a.device_name || '').toLowerCase();
      const nameB = (b.device_name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  return filtered;
}, [devices, filterText, statusFilter, ownerFilter, searchField, user?.is_admin, user?.username]);

// ── Group filteredDevices into tabs ─────────────────────────────────────────
// Admin  → group by brand
// User   → group by branch
const groupedDevices = useMemo(() => {
  if (!filteredDevices.length) return [];
  const groups = [];
  const seen   = new Map();

  filteredDevices.forEach(device => {
    let key, label;
    if (user?.is_admin) {
      // Group by brand. brandLabels was a lookup keyed by username, which
      // cannot represent an owner with two brands - the whole reason the
      // model moved to brand_id.
      key   = (device.brand_name || 'zzz_no_brand').toLowerCase();
      label = device.brand_name || 'No Brand';
    } else {
      key = extractBranchName(device);
      if      (key === 'aaa_main')    label = 'Main';
      else if (key === 'zzz_no_folder') label = 'No Folder';
      else    label = key.charAt(0).toUpperCase() + key.slice(1).replace(/-/g, ' ');
    }
    if (!seen.has(key)) { seen.set(key, groups.length); groups.push({ key, label, devices: [] }); }
    groups[seen.get(key)].devices.push(device);
  });
  return groups;
}, [filteredDevices, user?.is_admin, extractBranchName, brandLabels]);

// Clean up stale refs when groups change.
// Only reset activeTabIndex if the current index is now out of range —
// do NOT blindly reset to 0 on every poll-triggered re-render.
useEffect(() => {
  sectionRefs.current   = sectionRefs.current.slice(0, groupedDevices.length);
  tabButtonRefs.current = tabButtonRefs.current.slice(0, groupedDevices.length);
  setActiveTabIndex(prev => (prev >= groupedDevices.length ? 0 : prev));
}, [groupedDevices]);

// Highlight the tab whose section is currently the topmost visible one.
// Scroll listener is more deterministic than IntersectionObserver here:
// we explicitly find which section's top has just passed under the sticky bar.
useEffect(() => {
  if (groupedDevices.length <= 1) return;

  const computeActive = () => {
    if (isScrollingToSection.current) return;
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const topbarH  = isMobile ? 52 : 0;
    const barH     = tabsBarRef.current?.offsetHeight ?? 46;
    // The line below the sticky bar; a section becomes "active" when its top
    // crosses (or has crossed) above this line.
    const cutoff = topbarH + barH + 8;

    let bestIdx = 0;
    for (let i = 0; i < sectionRefs.current.length; i++) {
      const el = sectionRefs.current[i];
      if (!el) continue;
      const top = el.getBoundingClientRect().top;
      if (top - cutoff <= 0) bestIdx = i;
      else break;
    }
    setActiveTabIndex(prev => (prev === bestIdx ? prev : bestIdx));
  };

  let raf = 0;
  const onScroll = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => { raf = 0; computeActive(); });
  };

  computeActive();                                          // initial
  // Sections may not be laid out on first render (images loading,
  // device cards mounting). Re-run shortly after so the active pill
  // appears even when scroll position hasn't changed yet.
  const t1 = setTimeout(computeActive, 200);
  const t2 = setTimeout(computeActive, 800);
  // .sb-main is the real scroll container; fall back to window
  const sc = document.querySelector('.sb-main') || window;
  sc.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);
  return () => {
    clearTimeout(t1); clearTimeout(t2);
    if (raf) cancelAnimationFrame(raf);
    sc.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onScroll);
  };
}, [groupedDevices]);

// Auto-scroll the tabs bar so the active tab is always centered as the user
// scrolls the page. Uses scrollerRef directly + getBoundingClientRect math.
useEffect(() => {
  const btn      = tabButtonRefs.current[activeTabIndex];
  const scroller = scrollerRef.current;
  if (!btn || !scroller) return;
  const btnRect      = btn.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  const btnLeftInScroller = btnRect.left - scrollerRect.left + scroller.scrollLeft;
  const target = btnLeftInScroller - (scroller.clientWidth / 2) + (btnRect.width / 2);
  const max    = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  scroller.scrollTo({
    left: Math.max(0, Math.min(target, max)),
    behavior: 'smooth',
  });
}, [activeTabIndex]);

// Drag-to-scroll handlers (used by JSX onMouseDown on .tabs-scroll-area).
// We attach mousemove/mouseup to document INSIDE onMouseDown so they catch
// movement even when the cursor leaves the bar.
const onTabsBarMouseDown = useCallback((e) => {
  if (e.button !== 0) return;
  const scroller = scrollerRef.current;
  if (!scroller) return;
  dragRef.current.down            = true;
  dragRef.current.startX          = e.clientX;
  dragRef.current.startScrollLeft = scroller.scrollLeft;
  dragRef.current.moved           = 0;
  scroller.classList.add('dragging');
  e.preventDefault();             // stop native button-drag / text selection

  const onMove = (ev) => {
    if (!dragRef.current.down) return;
    const dx = ev.clientX - dragRef.current.startX;
    if (Math.abs(dx) > 2) {
      dragRef.current.moved = Math.max(dragRef.current.moved, Math.abs(dx));
      scroller.scrollLeft = dragRef.current.startScrollLeft - dx;
    }
  };
  const onUp = () => {
    dragRef.current.down = false;
    scroller.classList.remove('dragging');
    document.removeEventListener('mousemove', onMove);
    document.removeEventListener('mouseup',   onUp);
  };
  document.addEventListener('mousemove', onMove);
  document.addEventListener('mouseup',   onUp);
}, []);

// Suppress the click that fires right after a real drag so the tab under the
// pointer doesn't activate when the user was just scrolling the bar.
const onTabsBarClickCapture = useCallback((e) => {
  if (dragRef.current.moved > 5) {
    e.stopPropagation();
    e.preventDefault();
    dragRef.current.moved = 0;
  }
}, []);

// Mouse wheel: vertical scroll → horizontal scroll on the bar
const onTabsBarWheel = useCallback((e) => {
  if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    e.preventDefault();
    scroller.scrollLeft += e.deltaY;
  }
}, []);

// Scroll the page to a section when a tab is clicked.
// Locks IntersectionObserver while scroll is animating so the active tab
// doesn't flicker. Unlock happens when scroll actually stops (detected via
// scroll events going quiet for 120ms), with a 2 s hard cap as safety.
const scrollToSection = useCallback((index) => {
  const el = sectionRefs.current[index];
  if (!el) return;

  // Set the active tab immediately — don't wait for the IO
  setActiveTabIndex(index);

  // Block IO while scrolling
  isScrollingToSection.current = true;

  // Helper: clear any pending timers
  const clearTimers = () => {
    if (scrollingTimerRef.current) {
      clearTimeout(scrollingTimerRef.current);
      scrollingTimerRef.current = null;
    }
  };

  // Unlock once scroll has been quiet for 120 ms
  const unlock = (sc) => {
    clearTimers();
    sc.removeEventListener('scroll', onScroll);
    isScrollingToSection.current = false;
  };
  const onScroll = (sc) => () => {
    clearTimers();
    scrollingTimerRef.current = setTimeout(() => unlock(sc), 120);
  };

  // .sb-main is the real scroll container (overflow-y: auto); fall back to window
  const sc = document.querySelector('.sb-main') || window;
  const scrollTop = sc === window ? window.scrollY : sc.scrollTop;

  // Hard cap: 2 s in case scroll event never fires (edge case)
  clearTimers();
  scrollingTimerRef.current = setTimeout(() => unlock(sc), 2000);
  sc.addEventListener('scroll', onScroll(sc), { passive: true });

  // Scroll: account for sticky tabs bar
  const barH   = tabsBarRef.current?.offsetHeight ?? 46;
  const offset = barH + 8;
  const top    = el.getBoundingClientRect().top + scrollTop - offset;
  sc.scrollTo({ top, behavior: 'smooth' });
}, []);

  // When navigating from branch TVs button: scroll to + highlight that user's section.
  // If the event also carried a branchId, light up the matching device cards
  // for ~5s so the admin can spot them immediately.
  useEffect(() => {
    if (!pendingScrollUser || !groupedDevices.length) return;
    const idx = groupedDevices.findIndex(g => g.key === pendingScrollUser.toLowerCase());
    if (idx === -1) return;
    setPendingScrollUser(null);
    // Activate the branch highlight (if any) once we start scrolling.
    if (pendingHighlightBranchId) {
      setHighlightBranchId(pendingHighlightBranchId);
      setPendingHighlightBranchId(null);
      setTimeout(() => setHighlightBranchId(null), 5000);
    }
    // Retry until sectionRefs[idx] is in the DOM (home view may still be mounting).
    const tryScroll = (attempts) => {
      if (sectionRefs.current[idx]) {
        scrollToSection(idx);
      } else if (attempts < 15) {
        setTimeout(() => tryScroll(attempts + 1), 100);
      }
    };
    setTimeout(() => tryScroll(0), 50);
  }, [pendingScrollUser, groupedDevices, scrollToSection, pendingHighlightBranchId]);

  // Event handlers
  const handleFilterChange = useCallback((event) => { 
    setFilterText(event.target.value); 
  }, []);

  const handleStatusFilterChange = useCallback((status) => {
    setStatusFilter(status);
  }, []);

  // Quick-filter to a single owner when clicking × on a card's owner badge (admin only)
  const handleOwnerFilterClick = useCallback((owner) => {
    setOwnerFilter(owner || '');
  }, []);

  const handleLoginSuccess = useCallback((userData, token) => {
  console.log("Login successful, setting user and token");
  // Always land on the home view — close any panel that was open before logout
  setShowAdminPanel(false);
  setShowFileManager(false);
  setShowGroupManager(false);
  setShowTimelineBackup(false);
  setShowMenuManager(false);
  setUser(userData);

  if (userData && userData.username) {
    webdavService.username = userData.username;
    webdavService.fetchCredentials().then(success => {
      if (success) {
        console.log('Successfully fetched WebDAV credentials after login');
      } else {
        console.warn('Failed to fetch WebDAV credentials after login');
      }
    });
  }

  setIsLoading(true);
  setTimeout(() => {
    console.log("Fetching devices after login");
    Promise.resolve(fetchDevicesOptimized(token)).finally(() => {
      setPendingDeviceInfoRefresh(true);
    });
  }, 0);
}, [fetchDevicesOptimized]);

  const handleLogout = useCallback(() => {
    console.log('?? User logging out...');

    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    try { sessionStorage.removeItem('pm_active_panel'); } catch {}
    try { sessionStorage.removeItem('menu_path'); } catch {}
    
    webdavService.clearCredentials();
    
    // Close WebSocket connection and clean up global reference
    if (wsRef.current) {
      wsRef.current.close();
    }
    setWsConnection(null);
  }, []);

// Handle home navigation - closes all panels and returns to main dashboard
const handleGoHome = useCallback(() => {
  console.log('Going home - closing all panels');

  // Close all panels and modals to return to main dashboard
  setShowAdminPanel(false);
  setShowFileManager(false);
  setShowGroupManager(false);
  setShowTimelineBackup(false);
  setShowMenuManager(false);          // ← also close menu-app (request #5)
  setShowDeviceModal(false);
  setShowPreviewModal(false);
  setDeviceForFileManagerModal(null);
  setIsAddingDevice(false);
  setIsEditingDevice(false);
  setSelectedDeviceState(null);
  setShowActionsMenu(null);

  // Clear any filters to show all devices
  setFilterText('');
  setStatusFilter('all');
  setOwnerFilter('');

  // Clear any cross-app navigation stash so we land on a clean main page
  try { sessionStorage.removeItem('pm_menu_filter_user'); } catch (e) {}
  try { delete window.__menuapp_initial_path; } catch (e) {}

  console.log('Successfully returned to main dashboard');
}, []);

  // Toggle admin panel
  const toggleAdminPanel = useCallback(() => {
    console.log("Toggling Admin Panel");
    setShowAdminPanel((prev) => {
      const next = !prev;
      if (next) {
        setShowFileManager(false);
        setShowGroupManager(false);
        setShowTimelineBackup(false);
        setShowDeviceModal(false);
        setShowMenuManager(false);
      }
      return next;
    });
  }, []);

  const goToEmptyDevices = useCallback(() => {
    // Show Admin panel, then ask it to switch to Storage?Empty
    setShowAdminPanel(true);
    // Use a short microtask so AdminPanel’s ref is ready
    setTimeout(() => {
      adminPanelRef.current?.openStorageEmpty?.();
    }, 0);
  }, []);

  // NEW: Add handler for WebDAV monitor toggle
  const toggleWebdavMonitor = useCallback(() => {
    // This is a placeholder. The actual logic will be inside the AdminPanel component.
    // The main app only needs to know about AdminPanel, which contains the monitor.
  }, []);

  // Toggle file manager
  const toggleFileManager = useCallback(() => {
    console.log("Toggling File Manager");
    setShowFileManager((prev) => {
      const next = !prev;
      if (next) {
        setShowAdminPanel(false);
        setShowGroupManager(false);
        setShowTimelineBackup(false);
        setShowMenuManager(false);
      }
      return next;
    });
  }, []);


  // Listen for menu:close event dispatched by menu-app logo click
  useEffect(() => {
    const handler = () => setShowMenuManager(false);
    window.addEventListener('menu:close', handler);
    return () => window.removeEventListener('menu:close', handler);
  }, []);

  // pm:back — dispatched by menu-app back button (user-mode Branches page)
  // Closes menu manager and reopens AdminPanel so the user lands back where they came from.
  useEffect(() => {
    const handler = () => { setShowMenuManager(false); setShowAdminPanel(true); };
    window.addEventListener('pm:back', handler);
    return () => window.removeEventListener('pm:back', handler);
  }, []);

  // Handle navigation requests from AdminPanel user-card stat clicks.
  // Stats emit `pm:nav` with { kind: 'brands'|'branches'|'devices', userId, username }.
  useEffect(() => {
    const onNav = (e) => {
      const { kind, userId, username, branchId } = e.detail || {};
      if (!kind) return;
      if (kind === 'devices') {
        // Close all panels, return to home, then scroll to user section.
        // If event includes branchId, mark that branch's devices for highlight
        // after the user section finishes scrolling into view.
        setShowAdminPanel(false);
        setShowMenuManager(false);
        setShowFileManager(false);
        setShowGroupManager(false);
        setShowTimelineBackup(false);
        setOwnerFilter('');
        setPendingScrollUser(username || null);
        setPendingHighlightBranchId(branchId || null);
        return;
      }
      // Brands / Branches → open Menu Manager at the right inner route.
      let target, navOpts;
      if (kind === 'brands') {
        target = '/brands';
        navOpts = { state: { filterUser: userId, filterUsername: username } };
      } else if (kind === 'branches') {
        const { brandId, brandCount } = e.detail || {};
        if (brandId && brandCount === 1) {
          // Single brand → go directly to that brand's branches page
          target = `/brands/${brandId}/branches`;
          navOpts = {};
        } else {
          // Multiple brands or no brand → show filtered brands list so user can pick
          target = '/brands';
          navOpts = { state: { filterUser: userId, filterUsername: username } };
        }
      } else {
        target = '/';
        navOpts = {};
      }
      // For already-mounted menu-app (dispatched immediately)
      window.dispatchEvent(new CustomEvent('menuapp:navigate', { detail: { path: target, opts: navOpts } }));
      // For fresh mount (menu-app not yet rendered when event fires)
      window.__menuapp_initial_path = target;
      window.__menuapp_initial_opts = navOpts;
      sessionStorage.setItem('pm_menu_filter_user', JSON.stringify({ userId, username }));
      setShowAdminPanel(false);
      setShowFileManager(false);
      setShowGroupManager(false);
      setShowTimelineBackup(false);
      setShowMenuManager(true);
    };
    window.addEventListener('pm:nav', onNav);
    return () => window.removeEventListener('pm:nav', onNav);
  }, []);

  // Cross-app handoff: open AdminPanel → User Manager. Triggered from menu-app
  // (e.g. clicking an assigned manager in the Edit Brand modal). The preset
  // payload lives in sessionStorage; AdminPanel consumes it on mount.
  useEffect(() => {
    const onOpenUM = () => {
      setShowMenuManager(false);
      setShowFileManager(false);
      setShowGroupManager(false);
      setShowTimelineBackup(false);
      setShowAdminPanel(true);
    };
    window.addEventListener('pm:open-user-manager', onOpenUM);
    return () => window.removeEventListener('pm:open-user-manager', onOpenUM);
  }, []);

  // Toggle menu manager
  const toggleMenuManager = useCallback(() => {
    console.log("Toggling Menu Manager");
    setShowMenuManager((prev) => {
      const next = !prev;
      if (next) {
        setShowAdminPanel(false);
        setShowFileManager(false);
        setShowGroupManager(false);
        setShowTimelineBackup(false);
      }
      return next;
    });
  }, []);

const toggleGroupManager = useCallback(() => {
  console.log("Toggling Group Manager");
  setShowGroupManager((prev) => {
    const next = !prev;
    if (next) {
      setShowAdminPanel(false);
      setShowFileManager(false);
      setShowTimelineBackup(false);
      setShowDeviceModal(false);
    }
    return next;
  });
}, []);

const toggleTimelineBackup = useCallback(() => {
    console.log("Toggling Timeline Backup");
    setShowTimelineBackup(true);
    setShowAdminPanel(false);
    setShowFileManager(false);
    setShowGroupManager(false);
  }, []);

  // Handle adding a new device
  const handleAddDevice = useCallback(() => {
    if (!user || !user.is_admin) return;
    
    setDeviceFormData({
      device_name: '',
      description: '',
      register_date: new Date().toISOString().split('T')[0],
      anydesk_id: '',
      anydesk_password: '',
      media_files_link: `${API_BASE_URL}/home/`,
      brand_id: '',
      branch_id: '',
    });

    setIsAddingDevice(true);
    setShowDeviceModal(true);
    setIsEditingDevice(false);
    setSelectedDeviceState(null);
    setShowPassword(false);
    refreshOwners();
  }, [user, refreshOwners]); // eslint-disable-line

  // Handle device actions menu toggle - updated for device name clicks
  const toggleActionsMenu = useCallback((deviceName) => {
    setShowActionsMenu(null);
    
    if (showActionsMenu === deviceName) {
      return;
    }
    
    setTimeout(() => {
      setShowActionsMenu(deviceName);
    }, 50);
  }, [showActionsMenu]);

  // Handle duplicating a device
  const handleDuplicateDevice = useCallback((device) => {
    if (!user || !user.is_admin) return;
    
    const newName = `${device.device_name}-copy`;
    
    let newMediaFilesLink;
    if (device.media_files_link && device.media_files_link !== `${API_BASE_URL}/home/${device.device_name}`) {
      newMediaFilesLink = device.media_files_link.replace(device.device_name, newName);
    } else {
      newMediaFilesLink = `${API_BASE_URL}/home/${newName}`;
    }
    
    // 2026-05-19 Phase C — clone preserves the source's brand/branch
    // assignment so the new placeholder lives in the same org bucket. Admin
    // can change either before saving. device_uid is NOT cloned — backend
    // computes a fresh one (with the brand/branch slug prefix if the new
    // device_name collides globally).
    setDeviceFormData({
      device_name: newName,
      description: device.description || '',
      register_date: device.register_date || new Date().toISOString().split('T')[0],
      anydesk_id: device.anydesk_id || '',
      anydesk_password: device.anydesk_password || '',
      frp_port: device.frp_port || '',
      media_files_link: newMediaFilesLink,
      brand_id:  device.brand_id  || '',
      branch_id: device.branch_id || '',
    });
    
    console.log("Duplicating device with owner:", device.owner_username);
    setSelectedOwner(device.owner_username || '');
    
    setIsAddingDevice(true);
    setShowDeviceModal(true);
    setIsEditingDevice(false);
    setSelectedDeviceState(null);
    setShowActionsMenu(null);
    setShowPassword(false);
  }, [user]);

  // Update media_files_link when device_name changes
  const handleDeviceNameChange = useCallback((e) => {
    const newDeviceName = e.target.value;
    setDeviceFormData(prev => ({
      ...prev, 
      device_name: newDeviceName,
      media_files_link: prev.media_files_link === '' 
        || prev.media_files_link === `${API_BASE_URL}/home/` 
        || prev.media_files_link === `${API_BASE_URL}/home/${prev.device_name}`
        ? `${API_BASE_URL}/home/${newDeviceName}`
        : prev.media_files_link
    }));
  }, []);

  // Handle saving a new device
  const handleSaveDevice = useCallback(() => {
    if (!user || !user.is_admin) return;
    
    setIsLoading(true);
    
    const deviceData = {
      device_name: deviceFormData.device_name,
      description: deviceFormData.description,
      register_date: deviceFormData.register_date,
      anydesk_id: deviceFormData.anydesk_id,
      anydesk_password: deviceFormData.anydesk_password,
      frp_port: deviceFormData.frp_port,
      media_files_link: deviceFormData.media_files_link,
      // 2026-05-19 Phase C — '' → null so the backend treats it as
      // "unassigned" instead of NaN. Both fields are optional today; admin
      // can leave them blank and the device lives at NULL/NULL until later
      // edit assigns it to a (brand, branch).
      brand_id:  deviceFormData.brand_id  || null,
      branch_id: deviceFormData.branch_id || null,
      owner_username: selectedOwner
    };

    const token = localStorage.getItem('token');
    const ownerMapping = selectedOwner ? true : false;
    console.log("Selected owner for new device:", selectedOwner);

    fetch(`${API_BASE_URL}/api/devices`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(deviceData),
      credentials: 'include'
    })
    .then(response => {
      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('A device with this name already exists.');
        }
        throw new Error(`Server error: ${response.status}`);
      }
      return response.json();
    })
    .then(newDevice => {
      setDevices(prevDevices => [...prevDevices, newDevice]);
      
      if (ownerMapping && selectedOwner) {
        return fetch(`${API_BASE_URL}/api/admin/device-mapping/update`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            client_username: selectedOwner,
            device_name: newDevice.device_name
          }),
          credentials: 'include'
        })
        .then(mapResponse => {
          if (!mapResponse.ok) {
            console.error("Failed to set owner mapping");
            throw new Error('Failed to set owner mapping');
          }
          console.log(`Owner ${selectedOwner} mapped to device ${newDevice.device_name}`);
          return newDevice;
        });
      }
      return newDevice;
    })
    .then(newDevice => {
  // Add the new device to the current devices list immediately
  setDevices(prevDevices => [...prevDevices, newDevice]);
  
  alert(`Device "${newDevice.device_name}" created successfully.`);
  
  // Also refresh from server to ensure consistency
  fetchDevicesOptimized();
  
  setShowDeviceModal(false);
  setIsAddingDevice(false);
})
    .catch(err => {
      console.error('Error creating device:', err);
      alert(`Failed to create device: ${err.message}`);
    })
    .finally(() => {
      setIsLoading(false);
    });
  }, [user, deviceFormData, selectedOwner, fetchDevicesOptimized]);

  // Handle updating an existing device
  const handleUpdateDevice = useCallback(() => {
    if (!user || !user.is_admin) return;
    
    setIsLoading(true);

// Validation: Check for empty or duplicate device name
    const trimmedNewName = deviceFormData.device_name.trim();
    
    if (!trimmedNewName) {
      alert('Device name cannot be empty.');
      setIsLoading(false);
      return;
    }
    
    // Check if name changed and if new name already exists
    const nameChanged = trimmedNewName !== selectedDeviceState.device_name;
    if (nameChanged) {
      const nameExists = devices.some(d => 
        d.device_name === trimmedNewName && d.device_name !== selectedDeviceState.device_name
      );
      
      if (nameExists) {
        alert(`A device with the name "${trimmedNewName}" already exists. Please choose a different name.`);
        setIsLoading(false);
        return;
      }
    }

    const updateDeviceOwner = (deviceName, newOwner) => {
      if (!newOwner) {
        const token = localStorage.getItem('token');
        fetch(`${API_BASE_URL}/api/admin/device-mapping/device/${deviceName}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include'
        })
        .then(response => {
          if (!response.ok && response.status !== 404) {
            throw new Error(`Server error: ${response.status}`);
          }
          alert(`Device "${deviceName}" updated successfully. Owner mapping removed.`);
fetchDevicesOptimized();
// Close the modal and return to main view
setShowDeviceModal(false);
setIsEditingDevice(false);
setSelectedDeviceState(null);
        })
        .catch(err => {
          console.error('Error removing device mapping:', err);
          alert(`Failed to update device owner: ${err.message}`);
        })
        .finally(() => {
          setIsLoading(false);
        });
      } else {
        const token = localStorage.getItem('token');
        fetch(`${API_BASE_URL}/api/admin/device-mapping/update`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            client_username: newOwner,
            device_name: deviceName
          }),
          credentials: 'include'
        })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Server error: ${response.status}`);
          }
          alert(`Device "${deviceName}" updated successfully. Owner set to ${newOwner}.`);
fetchDevicesOptimized();
// Close the modal and return to main view
setShowDeviceModal(false);
setIsEditingDevice(false);
setSelectedDeviceState(null);
        })
        .catch(err => {
          console.error('Error updating device mapping:', err);
          alert(`Failed to update device owner: ${err.message}`);
        })
        .finally(() => {
          setIsLoading(false);
        });
      }
    };

    const updateData = {
      device_name: trimmedNewName,
      description: deviceFormData.description,
      register_date: deviceFormData.register_date,
      anydesk_id: deviceFormData.anydesk_id,
      anydesk_password: deviceFormData.anydesk_password,
      frp_port: deviceFormData.frp_port,
      media_files_link: deviceFormData.media_files_link,
    };

    // 2026-11-03 — OMIT these keys when the form has no value, do not send
    // null. The backend reads an explicit null as "unset this device's brand",
    // while an absent key means "leave it as it is". Sending `|| null` meant
    // any save made while the Brand box was empty silently wiped a brand and
    // branch that were already correct -- which is how pavlova-01 lost both
    // after its topic was changed. The Brand box is derived from the owner and
    // is not always populated in the form, so empty must never mean erase.
    if (deviceFormData.brand_id)  updateData.brand_id  = deviceFormData.brand_id;
    if (deviceFormData.branch_id) updateData.branch_id = deviceFormData.branch_id;

    // 2026-05-19 — URL keys on device_uid (the immutable PK). For pre-Phase-C
    // legacy rows device_uid == device_name so the existing reference holds;
    // for any row that's been renamed via the new flow we use the UID we
    // already have in state. Backend accepts either form via getDeviceByName.
    const originalDeviceIdentifier = selectedDeviceState.device_uid || selectedDeviceState.device_name;
    const token = localStorage.getItem('token');

    fetch(`${API_BASE_URL}/api/devices/${originalDeviceIdentifier}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updateData),
      credentials: 'include'
    })
    .then(response => {
      if (!response.ok) {
        if (response.status === 409) {
          return response.json().then(errData => {
            throw new Error(errData.error || 'A device with this name already exists.');
          });
        }
        throw new Error(`Server error: ${response.status}`);
      }
      return response.json();
    })
    .then(async updatedDevice => {
  // Update devices list - handle both rename and regular update
  setDevices(prevDevices =>
    prevDevices.map(device =>
      device.device_name === selectedDeviceState.device_name ? updatedDevice : device
    )
  );

  // Update selectedDeviceState with new device data (including new name if renamed)
  setSelectedDeviceState(updatedDevice);

  // 2026-05-19 — push the renamed friendly label to the agent's
  // UserSettings.DEVICE_NAME so the agent's own settings page reflects
  // it. Uses the immutable UID for MQTT routing. If the device is
  // offline (or the agent rejects the call), the DB rename still stands
  // — we just warn the admin so they know the agent UI is stale until
  // the device reconnects. Reload the agent app once the setting lands
  // so it picks up the new label without waiting for a manual reboot.
  const pushedNewName = (updatedDevice.device_name !== selectedDeviceState.device_name);
  if (pushedNewName && updatedDevice.is_online) {
    const mqttId = updatedDevice.device_uid || updatedDevice.device_name;
    try {
      promediaService.setDevice(mqttId);
      await promediaService.setSettingsValue(mqttId, 'device_name', updatedDevice.device_name);
      // Reload best-effort — failure here is harmless (next agent boot picks up the change).
      promediaService.reloadApp().catch(() => {});
    } catch (pushErr) {
      console.warn('Device-name push to agent failed:', pushErr?.message);
      alert(
        `Device updated in DB, but the agent setting couldn't be synced ` +
        `right now.\n\nReason: ${pushErr?.message || 'unknown'}\n\n` +
        `The new name will appear in the device's local settings the ` +
        `next time the agent comes online or restarts.`
      );
    }
  } else if (pushedNewName && !updatedDevice.is_online) {
    alert(
      `Device "${updatedDevice.device_name}" updated.\n\n` +
      `⚠️  The device is offline. The new name will be pushed to its ` +
      `settings the next time it connects via MQTT.`
    );
  }

  // 2026-05-20 — Unified state: if the media_files_link changed AND the
  // agent already has a WebDAV sync configured, push the new URL onto it
  // so File Sync + Edit Player + agent all agree. We keep the existing
  // username / password / folder / clearFolder so admin doesn't have to
  // re-enter credentials. If no sync exists yet, the new URL is just
  // saved in DB and will pre-fill the Add-sync form next time it's opened.
  const linkChanged = (updatedDevice.media_files_link !== selectedDeviceState.media_files_link);
  if (linkChanged && updatedDevice.is_online && updatedDevice.media_files_link) {
    const mqttId = updatedDevice.device_uid || updatedDevice.device_name;
    try {
      const existing = await promediaService.getGrabberWebdav(mqttId).catch(() => null);
      if (existing && existing.id) {
        await promediaService.saveGrabberWebdav(mqttId, {
          id: existing.id,
          url: updatedDevice.media_files_link,
          username: existing.username || '',
          password: existing.password || '',
          folder: existing.folder || '/',
          clearFolder: !!existing.clearFolder,
        });
      }
    } catch (pushErr) {
      console.warn('Grabber URL sync to agent failed:', pushErr?.message);
      // Non-fatal — the DB has the new URL. Admin can re-save in
      // Player Manager → File Sync to push it to the agent later.
    }
  }

  // Handle owner mapping update - use NEW device name from updatedDevice
  if (selectedOwner !== selectedDeviceState.owner_username) {
    updateDeviceOwner(updatedDevice.device_name, selectedOwner);
  } else {
    if (!pushedNewName) alert(`Device "${updatedDevice.device_name}" updated successfully.`);
    // Refresh device list to ensure consistency
    fetchDevicesOptimized();
    // Close the modal and return to main view
    setShowDeviceModal(false);
    setIsEditingDevice(false);
    setSelectedDeviceState(null);
  }
})
    .catch(err => {
      console.error('Error updating device:', err);
      alert(`Failed to update device: ${err.message}`);
      setIsLoading(false);
    });
  }, [user, deviceFormData, selectedDeviceState, selectedOwner, fetchDevicesOptimized]);

  // Handle device deletion
  const handleDelete = useCallback((deviceName) => {
    if (!user || !user.is_admin) return;
    
    if (!deviceName) return;
    if (window.confirm(`Are you sure you want to delete the device "${deviceName}"?`)) {
      const token = localStorage.getItem('token');
      const deleteUrl = `${API_BASE_URL}/api/devices/${deviceName}`;
      
      fetch(deleteUrl, { 
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      })
      .then(response => {
        if (response.ok) {
          setDevices(prevDevices => prevDevices.filter(d => d.device_name !== deviceName));
          alert(`Device "${deviceName}" deleted successfully.`);
          return;
        } else {
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            return response.json().then(errData => { 
              throw new Error(errData.error || `Server error (status ${response.status})`); 
            });
          } else {
            return response.text().then(textData => {
              let errorMsg = `Server returned status ${response.status}.`;
              const titleMatch = textData.match(/<title>(.*?)<\/title>/i);
              if (titleMatch && titleMatch[1]) { 
                errorMsg += ` Server message: ${titleMatch[1]}`; 
              } else if (textData.length < 200) { 
                errorMsg += ` Response: ${textData}`; 
              }
              throw new Error(errorMsg);
            });
          }
        }
      })
      .catch(err => { 
        console.error(`Failed to delete ${deviceName}:`, err); 
        alert(`Error deleting device "${deviceName}". ${err.message}`); 
      });
    }
  }, [user]);

  // Handle device name click to show RemoteControl modal
  const handleDeviceNameClick = useCallback((device) => {
  console.log('?? Opening Remote Control for device:', device.device_name);
  setSelectedDeviceForRemote(device);
  setShowRemoteControlModal(true);
}, []);

  // Handle edit device
  const handleEditDevice = useCallback((device) => {
    if (!user || !user.is_admin) return;
    
    const index = filteredDevices.findIndex(d => d.device_name === device.device_name);
    setDeviceIndex(index);
    
    const defaultMediaLink = `${API_BASE_URL}/home/${device.device_name}`;
    
    setSelectedDeviceState(device);
    // 2026-05-20 — clear the adoption-success badge from any previous
    // session; only show it for the run that actually performed the swap.
    setJustAdoptedUid(null);
    setDeviceFormData({
      device_name: device.device_name,
      description: device.description || '',
      register_date: device.register_date || new Date().toISOString().split('T')[0],
      anydesk_id: device.anydesk_id || '',
      anydesk_password: device.anydesk_password || '',
      frp_port: device.frp_port || '',
      media_files_link: device.media_files_link || defaultMediaLink,
      // 2026-05-19 Phase C — preselect brand/branch on edit so the cascade
      // dropdowns open with the device's current org assignment. Backend
      // returns these as integers (or null) from the devices SELECT.
      brand_id:  device.brand_id  || '',
      branch_id: device.branch_id || '',
    });
    // 2026-05-19 — let the auto-derive effect re-compute the canonical path
    // /<owner>/<branch>/<device_name> on edit open. Admin can override after,
    // but the field always opens showing the current convention so they can
    // spot legacy / drifted paths and either accept or override them.
    setMediaFolderTouched(false);

    setSelectedOwner(device.owner_username || '');

    // 2026-05-19 — fetch orphans so the Adopt selector has fresh options every
    // time admin opens the Edit modal. Cheap query, scoped to admin endpoints.
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const r = await fetch(`${API_BASE_URL}/api/devices/orphans`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (r.ok) {
          const list = await r.json();
          setOrphanDevices(Array.isArray(list) ? list : []);
        }
      } catch (e) { /* silent — Adopt dropdown just shows empty */ }
    })();

    setIsEditingDevice(true);
    setShowDeviceModal(true);
    setIsAddingDevice(false);
    setShowActionsMenu(null);
    setShowPassword(false);
  }, [user, filteredDevices]);

  // Navigation functions for device modal
  const goToPreviousDevice = useCallback(() => {
    if (deviceIndex > 0) {
      const prevIndex = deviceIndex - 1;
      const prevDevice = filteredDevices[prevIndex];
      handleEditDevice(prevDevice);
    }
  }, [deviceIndex, filteredDevices, handleEditDevice]);

  const goToNextDevice = useCallback(() => {
    if (deviceIndex < filteredDevices.length - 1) {
      const nextIndex = deviceIndex + 1;
      const nextDevice = filteredDevices[nextIndex];
      handleEditDevice(nextDevice);
    }
  }, [deviceIndex, filteredDevices, handleEditDevice]);

  // 2026-05-19 — Adopt selector handler. Picks an orphan UID (real auto-
  // registered device) and POSTs to the adopt endpoint, which atomically
  // moves the placeholder's metadata onto the orphan row and deletes the
  // placeholder. After success we refresh the device list and close modal.
  const handleAdoptDevice = useCallback(async (fromUid) => {
    if (!fromUid || !selectedDeviceState?.device_uid) return;
    const placeholderUid = selectedDeviceState.device_uid;
    const orphan = orphanDevices.find(o => o.device_uid === fromUid);
    const orphanLabel = orphan
      ? `${orphan.device_uid}${orphan.is_online ? ' (online)' : ''}${orphan.device_model ? ` — ${orphan.device_model}` : ''}`
      : fromUid;
    const ok = window.confirm(
      `Adopt this placeholder into a real device?\n\n` +
      `Placeholder: ${selectedDeviceState.device_name} (${placeholderUid})\n` +
      `Real device: ${orphanLabel}\n\n` +
      `The real device's MQTT topic will be renamed to match the device name ` +
      `(falls back to keeping ${fromUid} if that name is taken or the device is offline); ` +
      `the placeholder row will be deleted, and the brand/branch/AnyDesk/folder settings ` +
      `you saved on the placeholder will move onto the real device.\n\nContinue?`
    );
    if (!ok) return;
    setAdoptingFromUid(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_BASE_URL}/api/devices/${encodeURIComponent(placeholderUid)}/adopt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ from_uid: fromUid }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        window.alert(`Adopt failed: ${body.error || r.status}`);
        return;
      }

      // 2026-05-19 — push the adopted display name onto the agent so its
      // local web UI shows the human-friendly name immediately, then reload
      // the app so the change takes effect without waiting for a manual
      // reboot. Best-effort: a failure here is non-fatal — the DB swap has
      // already happened and the new name will appear next time the agent
      // restarts on its own. Only run if the real device is online (the
      // placeholder is offline by definition, but the orphan should be live).
      const placeholderName = selectedDeviceState.device_name;
      if (orphan?.is_online && placeholderName) {
        try {
          promediaService.setDevice(fromUid);
          await promediaService.setSettingsValue(fromUid, 'device_name', placeholderName);
          promediaService.reloadApp().catch(() => {});
        } catch (pushErr) {
          console.warn('Adopt push to agent failed:', pushErr?.message);
          // Don't block the UI on this — just inform admin so they know
          // the agent UI is briefly out-of-sync.
          window.alert(
            `Adopted in DB, but couldn't push the new name to the device ` +
            `right now. The device will pick it up on its next reboot.\n\n` +
            `Reason: ${pushErr?.message || 'unknown'}`
          );
        }
      }

      // 2026-10-24 — owner asked: the adopted device's MQTT topic should
      // match its human-friendly name instead of staying on the raw
      // MAC-derived orphan UID forever. Reuse the same rename path as
      // DeviceQuickManageModal's "Change MQTT Topic" action (PK rename +
      // ON UPDATE CASCADE onto client_device_map/device_permissions/
      // group_devices), then push the live device from its OLD topic to the
      // NEW one — same two-step dance saveMqttToken() does there. Best-effort
      // and non-blocking: only attempted while the device is online (no one
      // to deliver the topic-switch command to otherwise), and skipped
      // silently if the slug is empty or collides with an existing
      // device_uid — the device just keeps working on its raw ID, same as
      // before this change.
      let finalUid = fromUid;
      const topicSlug = String(placeholderName || '')
        .toLowerCase().trim()
        .replace(/[^a-z0-9._-]+/g, '-')
        .replace(/^-+|-+$/g, '');
      if (orphan?.is_online && topicSlug && topicSlug !== fromUid) {
        try {
          const tr = await fetch(`${API_BASE_URL}/api/devices/${encodeURIComponent(fromUid)}/change-mqtt-token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            credentials: 'include',
            body: JSON.stringify({ new_token: topicSlug }),
          });
          const trBody = await tr.json().catch(() => ({}));
          if (tr.ok) {
            promediaService.setDevice(fromUid);
            await promediaService.setSettingsValue(fromUid, 'device_token', topicSlug);
            promediaService.reloadApp().catch(() => {});
            finalUid = topicSlug;
          } else {
            console.warn('Adopt: MQTT topic rename skipped —', trBody.error || tr.status);
          }
        } catch (trErr) {
          console.warn('Adopt: MQTT topic rename failed —', trErr?.message);
        }
      }

      // 2026-05-20 — Keep the Edit modal OPEN after adoption so admin can
      // save any other dirty fields (brand_id/branch_id/etc.) the same way
      // they would on a regular edit. Previously we closed the modal here,
      // so any unsaved form fields (e.g. "I just assigned a new branch")
      // were lost — admin had to re-open and re-pick everything.
      // Patch the selected device's UID in place so subsequent saves
      // target the final UID (orphan's raw ID, or the renamed topic if that
      // succeeded above) — the placeholder is gone now either way — then
      // mark the adoption-success badge for the current modal session.
      setSelectedDeviceState(prev => prev ? { ...prev, device_uid: finalUid } : prev);
      setJustAdoptedUid(finalUid);
      // Drop the orphan we just consumed from the dropdown.
      setOrphanDevices(prev => prev.filter(o => o.device_uid !== fromUid));
      // Refresh device list in the background so the placeholder vanishes
      // from the player grid; the open Edit modal continues using the
      // patched selectedDeviceState.
      try { fetchDevicesOptimized(); } catch {}
    } catch (e) {
      window.alert(`Adopt failed: ${e.message}`);
    } finally {
      setAdoptingFromUid(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceState, orphanDevices]);

  const handleOwnerChange = useCallback((e) => {
    // 2026-05-19 — when admin switches Owner, brand_id/branch_id must be
    // cleared. Without this they keep pointing at the previous owner's brand,
    // and the branch dropdown ends up listing the wrong owner's branches.
    setSelectedOwner(e.target.value);
    setDeviceFormData(prev => ({ ...prev, brand_id: '', branch_id: '' }));
    setAvailableBranches([]);
  }, []);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

// NEW REMOTE CONTROL HANDLER
const handleRdpClick = useCallback((device) => {
  console.log('?? Opening Remote Control for device:', device.device_name);
  setSelectedDeviceForRemote(device);
  setShowRemoteControlModal(true);
  setShowActionsMenu(null); // Close the actions menu
}, []);

// Function to close the remote control modal
const handleCloseRemoteControl = useCallback(() => {
  setShowRemoteControlModal(false);
  setSelectedDeviceForRemote(null);
}, []);

// Open the Player Manager (Device Quick Manage) standalone — no Remote
// Control modal underneath. Admin-only shortcut from kebab / card menus.
const handleOpenPlayerManager = useCallback((device) => {
  setQuickManageDevice(device);
  setShowActionsMenu(null);
}, []);

  // ── Browser back/forward navigation through sidebar views ─────────────────
  // Each handleSetView call below pushes a history entry. popstate restores.
  // The back button walks back through visited views and STOPS at 'home' —
  // it never falls through to the browser's normal back navigation that would
  // exit the SPA. (Achieved by re-pushing 'home' when popstate lands on a
  // state with no pmView.)
  const handleSetViewRef = useRef(null);
  useEffect(() => {
    const onPop = (e) => {
      const view = e.state?.pmView;
      if (view) {
        handleSetViewRef.current?.(view, { fromPopState: true });
      } else {
        // popstate landed on a non-SPA history entry (or below home) — restore
        // home and re-push so further back presses still resolve to home.
        handleSetViewRef.current?.('home', { fromPopState: true });
        try { window.history.pushState({ pmView: 'home' }, '', window.location.href); } catch {}
      }
    };
    window.addEventListener('popstate', onPop);
    // Seed: replace current entry with home, then push home so the very first
    // back press still has somewhere to land WITHIN our SPA (instead of
    // navigating away). Net effect: 2 home entries on the back stack.
    if (typeof window !== 'undefined') {
      try {
        window.history.replaceState({ pmView: 'home' }, '', window.location.href);
        window.history.pushState({ pmView: 'home' }, '', window.location.href);
      } catch {}
    }
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // If user is not logged in, show login screen
  if (!user) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Derive active view from boolean states
  const activeView = showAdminPanel ? 'adminPanel'
    : showLiveStream ? 'liveStream'
    : showGroupManager ? 'groupManager'
    : showTimelineBackup ? 'backup'
    : showMenuManager ? 'menuManager'
    : showFileManager ? 'fileManager'
    : 'home';

  const handleSetView = (view, opts = {}) => {
    setShowAdminPanel(view === 'adminPanel' && !!user.is_admin);
    setShowFileManager(view === 'fileManager');
    setShowGroupManager(view === 'groupManager');
    setShowLiveStream(view === 'liveStream');
    setShowTimelineBackup(view === 'backup');
    setShowMenuManager(view === 'menuManager');
    // Push browser history so the back button reverses through visited views.
    // opts.fromPopState suppresses pushState when this call originates from a
    // popstate event (otherwise back would push another entry, looping).
    if (!opts.fromPopState && typeof window !== 'undefined') {
      try {
        const cur = window.history.state?.pmView;
        if (cur !== view) {
          window.history.pushState({ pmView: view }, '', window.location.href);
        }
      } catch {}
    }
  };
  // Expose the latest handleSetView to the popstate listener installed above.
  handleSetViewRef.current = handleSetView;

  // If admin panel is shown, render only that
  if (showAdminPanel && user.is_admin) {
    return (
      <Sidebar user={user} onLogout={handleLogout} activeView={activeView} onViewChange={handleSetView} adminPanelRef={adminPanelRef} hasFeature={hasProFeature}>
        <div className="App">
        <AdminPanel ref={adminPanelRef} />

        {showPreviewModal && previewItem && (
          <div className="preview-modal-overlay" onClick={() => setShowPreviewModal(false)}>
            {/* Modal content... */}
          </div>
        )}
        </div>
      </Sidebar>
    );
  }

  // If LiveStream is shown, render it standalone — no Sidebar/dashboard
  // chrome at all (not even the mobile topbar). Kiosk-style full-screen
  // camera page with its own close (×) button — same trap as GroupManager
  // below if this gets wrapped in <Sidebar>: the topbar eats into the video.
  // sign-${username} namespaces this box's streams from MAESTRO's on the
  // shared CT115 relay — .100 and .200 are different customer bases that can
  // otherwise collide on the same username.
  if (showLiveStream && user && hasProFeature('live_stream')) {
    return (
      <LiveStream
        user={{ ...user, username: `sign-${user.username}` }}
        visible={true}
        onClose={() => handleSetView('home')}
      />
    );
  }

// If GroupManager is shown, render only that
  if (showGroupManager && user && hasProFeature('groups')) {
    return (
      <Sidebar user={user} onLogout={handleLogout} activeView={activeView} onViewChange={handleSetView} adminPanelRef={adminPanelRef} hasFeature={hasProFeature}>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <GroupManager user={user} onClose={() => handleSetView('home')} />
        </div>

      </Sidebar>
    );
  }

  // If Timeline Backup is shown, render only that
  if (showTimelineBackup) {
    return (
      <Sidebar user={user} onLogout={handleLogout} activeView={activeView} onViewChange={handleSetView} adminPanelRef={adminPanelRef} hasFeature={hasProFeature}>
        <div className="timeline-backup-container" style={{ flex: 1, minHeight: 0 }}>
          <TimelineBackup onGoHome={() => handleSetView('home')} />
        </div>

      </Sidebar>
    );
  }

  // If MenuManager is shown, render only that.
  // 2026-05-17 — gate parity with GroupManager: only render the
  // menu sub-app when the user actually has the `menu` feature granted.
  // Admins always pass via hasProFeature.
  if (showMenuManager && user && hasProFeature('menu')) {
    const MenuApp = require('./menu-app/App').default;
    return (
      <Sidebar user={user} onLogout={handleLogout} activeView={activeView} onViewChange={handleSetView} adminPanelRef={adminPanelRef} hasFeature={hasProFeature}>
        <React.StrictMode>
          <MenuApp />
        </React.StrictMode>
      </Sidebar>
    );
  }

  // If FileManager is shown, render only that
  if (showFileManager) {
    return (
      <Sidebar user={user} onLogout={handleLogout} activeView={activeView} onViewChange={handleSetView} adminPanelRef={adminPanelRef} hasFeature={hasProFeature}>
        <div className="file-manager-container" style={{ position: 'fixed', top: 'var(--sb-top, 0px)', bottom: 0, left: 'var(--sb-width, 260px)', right: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <FileManager />
        </div>

      </Sidebar>
    );
  }
  
  // Main dashboard UI return statement
  return (
    <Sidebar user={user} onLogout={handleLogout} activeView={activeView} onViewChange={handleSetView} adminPanelRef={adminPanelRef} hasFeature={hasProFeature}>
    <div className="App">

      {/* Enhanced Info Panel */}
      <div className="enhanced-info-panel">
        {/* For Admin Users - Show full grid */}
        {user.is_admin && (
          <div className="info-grid">
            {/* Current Time Card - ADMIN ONLY */}
            <div className="info-card time-card">
              <div className="card-header">
                <svg className="card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                <span className="card-title">Current Time</span>
              </div>
              <div className="card-content">
                {new Date().toLocaleTimeString('en-US', { 
                  hour12: true, 
                  hour: '2-digit', 
                  minute: '2-digit', 
                  second: '2-digit' 
                })}
                <span className="live-indicator"></span>
              </div>
              <div className="card-subtitle">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
                {daysToCertExpiry !== null && (
                  <div className="cert-expiry-indicator">
                    <span className={`cert-days-remaining ${daysToCertExpiry <= 5 ? 'critical' : 'warning'}`}>
                      ({daysToCertExpiry})
                    </span>
                    " Days to renew Certbot
                  </div>
                )}
              </div>
            </div>

{/* Player Statistics Card with Pie Chart - ADMIN ONLY */}
<div className="info-card stats-card">
  <div className="card-header">
    <svg className="card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
      <line x1="8" y1="21" x2="16" y2="21"></line>
      <line x1="12" y1="17" x2="12" y2="21"></line>
    </svg>
    <span className="card-title">Players Status</span>
  </div>

  {/* Pill chips — same style as AdminPanel user-status chips */}
  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', alignItems: 'center', paddingTop: 4 }}>
    {(() => {
      // 2026-05-20 — Derive no-owner count + new-since-ack delta for the
      // "New" chip. Devices auto-register over MQTT with no client_username
      // when first seen by the broker; they sit in this pool until an admin
      // claims them via Add Device / Adopt.
      // A screen belongs to a BRAND; the username is only a login identity.
      // This counted devices with no owner_username, which after the brand
      // backfill reads 0 while unclaimed screens are the ones with no brand.
      const noOwnerCount = (devices || []).filter(d => !d.brand_id).length;
      const newSinceAck  = Math.max(0, noOwnerCount - (noOwnerAck || 0));
      return [
        { key: 'all',     label: 'Total',   count: deviceCounts.total,   dot: null,      bg: '#F1F5F9', activeBg: '#334155', color: '#475569', activeColor: '#fff', border: '#CBD5E1', activeBorder: '#334155', onClick: () => handleStatusFilterChange('all') },
        { key: 'online',  label: 'Online',  count: deviceCounts.online,  dot: '#2f80ed', bg: '#EFF6FF', activeBg: '#2f80ed', color: '#2f80ed', activeColor: '#fff', border: '#BFDBFE', activeBorder: '#2f80ed', onClick: () => handleStatusFilterChange('online') },
        { key: 'offline', label: 'Offline', count: deviceCounts.offline, dot: '#EF4444', bg: '#FEF2F2', activeBg: '#EF4444', color: '#EF4444', activeColor: '#fff', border: '#FECACA', activeBorder: '#EF4444', onClick: () => handleStatusFilterChange('offline') },
        { key: 'empty',   label: 'Empty',   count: emptyCount ?? 0,      dot: '#F97316', bg: '#FFF7ED', activeBg: '#F97316', color: '#EA580C', activeColor: '#fff', border: '#FED7AA', activeBorder: '#F97316', onClick: goToEmptyDevices },
        { key: 'new',     label: newSinceAck > 0 ? `New +${newSinceAck}` : 'New',
                          count: noOwnerCount,         dot: '#DC2626', bg: '#FEF2F2', activeBg: '#DC2626', color: '#B91C1C', activeColor: '#fff', border: '#FCA5A5', activeBorder: '#DC2626',
                          // Pulse the chip while there are un-acknowledged new orphans.
                          alert: newSinceAck > 0,
                          onClick: () => {
                            // Reveal the No-Owner devices: clear status
                            // filter + set ownerFilter = 'no-owner'.
                            try { setStatusFilter('all'); } catch {}
                            try { setOwnerFilter('no-owner'); } catch {}
                            ackNoOwner(noOwnerCount);
                          } },
      ];
    })().map(c => {
      const active = (c.key === 'new')
        ? ownerFilter === 'no-owner'
        : statusFilter === c.key;
      return (
        <button key={c.key} onClick={c.onClick} title={c.label}
          className={c.alert ? 'pm-newdev-chip pm-newdev-chip--pulse' : 'pm-newdev-chip'}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '5px 10px', borderRadius: 20, cursor: 'pointer',
            minWidth: 100,
            border: `1.5px solid ${active ? c.activeBorder : c.border}`,
            background: active ? c.activeBg : c.bg,
            color: active ? c.activeColor : c.color,
            fontWeight: active ? 700 : 500, fontSize: 12,
            transition: 'all 0.15s',
            boxShadow: active ? `0 2px 8px ${c.activeBg}55` : 'none',
          }}>
          {c.dot
            ? <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? '#fff' : c.dot, flexShrink: 0, display: 'inline-block' }} />
            : <span style={{ fontSize: 11 }}>📺</span>
          }
          <span style={{ letterSpacing: 0.2 }}>{c.label}</span>
          <span style={{
            fontWeight: 800, fontSize: 13,
            background: active ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.07)',
            borderRadius: 10, padding: '0px 6px', lineHeight: '18px',
          }}>{c.count}</span>
        </button>
      );
    })}
  </div>
</div>


{/* Enhanced System Monitoring Card - ADMIN ONLY */}
{user.is_admin && (
  <div className="info-card system-card">
    <div className="card-header">
      <svg className="card-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path>
      </svg>
      <span className="card-title">System</span>
    </div>
    
    <div className="system-monitor-container">
      {/* Storage Section */}
      <div className="monitor-section">
        <div className="monitor-header">
          <span className="monitor-label">STORAGE</span>
          <span className="monitor-value">{systemData.storage.free} GB Free</span>
        </div>
        <div className="monitor-bar-container">
          <div 
            className={`monitor-bar storage-bar ${systemData.storage.percentage > 80 ? 'bar-critical' : ''}`}
            style={{ width: `${systemData.storage.percentage}%` }}
          ></div>
        </div>
        <div className="monitor-sub">{systemData.storage.total} GB Total</div>
      </div>

      {/* RAM Section */}
      <div className="monitor-section">
        <div className="monitor-header">
          <span className="monitor-label">RAM</span>
          <span className="monitor-value">{systemData.ram.free} GB Free</span>
        </div>
        <div className="monitor-bar-container">
          <div 
            className={`monitor-bar ram-bar ${systemData.ram.percentage > 80 ? 'bar-critical' : ''}`}
            style={{ width: `${systemData.ram.percentage}%` }}
          ></div>
        </div>
        <div className="monitor-sub">{systemData.ram.total} GB Total</div>
      </div>

      {/* CPU Section */}
      <div className="monitor-section">
        <div className="monitor-header">
          <span className="monitor-label">CPU</span>
          <span className="monitor-value">{systemData.cpu.usage}% Load</span>
        </div>
        <div className="monitor-bar-container">
          <div 
            className={`monitor-bar cpu-bar ${systemData.cpu.usage > 80 ? 'bar-critical' : ''}`}
            style={{ width: `${Math.min(systemData.cpu.usage, 100)}%` }}
          ></div>
        </div>
        <div className="monitor-sub">{systemData.cpu.cores} Cores</div>
      </div>
    </div>
  </div>
)}
        </div>
        )}

        {/* Enhanced Filters Section */}
        <div className="enhanced-filters-section">
          <div className="filters-left">
            {/* For Regular Users - Show pill chip player stats */}
            {!user.is_admin && (
              <div className="user-stat-bar">
                {/* Brand logo */}
                {user.brand_logo && (
                  <div className="user-stat-logo">
                    <img src={user.brand_logo} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                )}
                {/* Pill chips */}
                <div className="user-stat-chips">
                  {[
                    { key: 'all',     label: 'Total',   count: deviceCounts.total,   dot: null,      bg: '#F1F5F9', activeBg: '#334155', color: '#475569', activeColor: '#fff', border: '#CBD5E1', activeBorder: '#334155', onClick: () => handleStatusFilterChange('all') },
                    { key: 'online',  label: 'Online',  count: deviceCounts.online,  dot: '#2f80ed', bg: '#EFF6FF', activeBg: '#2f80ed', color: '#2f80ed', activeColor: '#fff', border: '#BFDBFE', activeBorder: '#2f80ed', onClick: () => handleStatusFilterChange('online') },
                    { key: 'offline', label: 'Offline', count: deviceCounts.offline, dot: '#EF4444', bg: '#FEF2F2', activeBg: '#EF4444', color: '#EF4444', activeColor: '#fff', border: '#FECACA', activeBorder: '#EF4444', onClick: () => handleStatusFilterChange('offline') },
                  ].map(c => {
                    const active = statusFilter === c.key;
                    return (
                      <button key={c.key} onClick={c.onClick} title={c.label}
                        className="user-stat-chip"
                        style={{
                          border: `1.5px solid ${active ? c.activeBorder : c.border}`,
                          background: active ? c.activeBg : c.bg,
                          color: active ? c.activeColor : c.color,
                          fontWeight: active ? 700 : 500,
                          boxShadow: active ? `0 2px 8px ${c.activeBg}55` : 'none',
                        }}>
                        {c.dot
                          ? <span style={{ width: 7, height: 7, borderRadius: '50%', background: active ? '#fff' : c.dot, flexShrink: 0, display: 'inline-block' }} />
                          : <span style={{ fontSize: 11 }}>📺</span>
                        }
                        <span style={{ letterSpacing: 0.2 }}>{c.label}</span>
                        <span style={{
                          fontWeight: 800, fontSize: 13,
                          background: active ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.07)',
                          borderRadius: 10, padding: '0px 6px', lineHeight: '18px',
                        }}>{c.count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {user && user.is_admin && (
              <div className="filter-group">
                <label htmlFor="ownerFilter">Brand</label>
                <select 
                  id="ownerFilter" 
                  value={ownerFilter} 
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Brands</option>
                  <option value="no-owner">No Brand</option>
                  {uniqueBrands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>
            )}
            
            {/* 2026-05-19 — Unified search: lens icon + wide input + compact
                field selector + clear (×) button. No label — the icon is the
                affordance. Field selector picks which column the substring
                matches: name / UID / description / register_date. */}
            <div className="filter-group filter-group--search" style={{ flex: 1, minWidth: 240 }}>
              <div className="search-shell">
                <span className="search-shell__icon" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </span>
                <input
                  type="text"
                  id="playerFilter"
                  value={filterText}
                  onChange={handleFilterChange}
                  placeholder={
                    searchField === 'uid'           ? 'Enter UID letters…' :
                    searchField === 'description'   ? 'Enter description…' :
                    searchField === 'register_date' ? 'YYYY-MM-DD or partial…' :
                                                      'Enter player name letters…'
                  }
                  className="filter-input search-shell__input"
                />
                {filterText && (
                  <button
                    type="button"
                    className="search-shell__clear"
                    onClick={() => setFilterText('')}
                    title="Clear search"
                    aria-label="Clear search"
                  >×</button>
                )}
                <select
                  value={searchField}
                  onChange={(e) => setSearchField(e.target.value)}
                  className="filter-select search-shell__field"
                  title="Pick which field the search matches"
                >
                  <option value="name">Name</option>
                  <option value="uid">UID</option>
                  <option value="description">Desc.</option>
                  <option value="register_date">Date</option>
                </select>
              </div>
            </div>
            {/* Add Player Button */}
            {user && user.is_admin && (
              <button
                onClick={handleAddDevice}
                className='add-player-button'
                title='Add new player'
                style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#2196F3', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}
              >
                <svg xmlns='http://www.w3.org/2000/svg' width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'><line x1='12' y1='5' x2='12' y2='19'></line><line x1='5' y1='12' x2='19' y2='12'></line></svg>
                Add Player
              </button>
            )}
            {/* View-mode toggle (Cards | Table) — Cards is the default per design. */}
            <div className="ply-view-toggle" style={{
              display: 'inline-flex', background: '#F1F5F9',
              border: '1px solid #CBD5E1', borderRadius: 8, padding: 2,
              alignSelf: 'center',
            }}>
              {[
                { key: 'cards', label: 'Cards',
                  icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><rect x="3" y="3"  width="8" height="8" rx="1"/><rect x="13" y="3"  width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/></svg> },
                { key: 'table', label: 'Table',
                  icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M3 6h18M3 12h18M3 18h18"/></svg> },
              ].map(v => {
                const active = playersViewMode === v.key;
                return (
                  <button key={v.key} type="button" onClick={() => setPlayersViewMode(v.key)}
                    title={`${v.label} view`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      border: 'none',
                      background: active ? '#fff' : 'transparent',
                      color:      active ? '#1D4ED8' : '#475569',
                      boxShadow:  active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                    }}>
                    {v.icon}<span>{v.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Refresh Button - Available for all users */}
            {user && (
              <button
                onClick={async () => {
                  // First refresh the device list
                  fetchDevicesOptimized();
                  if (user.is_admin) {
                    fetchEmptyDevicesCount();
                  }
                  
                  // Then request fresh MQTT data from all online devices
                  try {
                    await refreshDeviceInfoForOnlineDevices(devices);
                  } catch (error) {
                    console.error('❌ Refresh error:', error);
                  }
                }}
                className="refresh-players-button"
                title="Refresh devices and request fresh data from all online devices"
                disabled={isLoading}
              >
                {isLoading ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spinning-icon">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 6v6l4 2"></path>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 4v6h-6"></path>
                    <path d="M1 20v-6h6"></path>
                    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10"></path>
                    <path d="M20.49 15a9 9 0 01-14.85 3.36L1 14"></path>
                  </svg>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && <p className="message">Loading devices...</p>}
      {error && <p className="message error-message">{error}</p>}

      {!isLoading && !error && playersViewMode === 'cards' && (
        <>
          {/* ── Sticky section tabs ── */}
          {groupedDevices.length > 1 && (
            <div className="device-section-tabs" ref={tabsBarRef}>
              <div
                className="tabs-scroll-area"
                ref={(el) => {
                  scrollerRef.current = el;
                  if (!el || el._dragWired) return;
                  el._dragWired = true;
                  // Imperative native-event drag wiring. React's synthetic
                  // onMouseDown was unreliable on this element (events
                  // appeared to be swallowed before reaching the handler),
                  // so we attach a real DOM listener directly.
                  let down = false, startX = 0, startSL = 0, moved = 0;
                  const onMove = (ev) => {
                    if (!down) return;
                    const dx = ev.clientX - startX;
                    if (Math.abs(dx) > 2) {
                      moved = Math.max(moved, Math.abs(dx));
                      dragRef.current.moved = moved;
                      el.scrollLeft = startSL - dx;
                      ev.preventDefault();
                    }
                  };
                  const onUp = () => {
                    down = false;
                    el.classList.remove('dragging');
                    window.removeEventListener('mousemove', onMove);
                    window.removeEventListener('mouseup', onUp);
                  };
                  el.addEventListener('mousedown', (e) => {
                    if (e.button !== 0) return;
                    down = true;
                    startX = e.clientX;
                    startSL = el.scrollLeft;
                    moved = 0;
                    dragRef.current.moved = 0;
                    el.classList.add('dragging');
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                  });
                  // Wheel: vertical scroll → horizontal scroll on the bar
                  el.addEventListener('wheel', (e) => {
                    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                      e.preventDefault();
                      el.scrollLeft += e.deltaY;
                    }
                  }, { passive: false });
                }}
                onClickCapture={onTabsBarClickCapture}
              >
                {groupedDevices.map((group, i) => (
                  <button
                    key={group.key}
                    ref={el => { tabButtonRefs.current[i] = el; }}
                    className={`section-tab-btn${activeTabIndex === i ? ' active' : ''}`}
                    onClick={() => scrollToSection(i)}
                  >
                    {/* Owner logo thumbnail */}
                    {(() => {
                      const ownerUsername = group.devices[0]?.owner_username;
                      const logo = ownerUsername && ownerLogoMap[ownerUsername];
                      return logo ? (
                        <img src={logo} alt="" style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'contain', background: '#fff', flexShrink: 0, verticalAlign: 'middle', marginRight: 2 }} />
                      ) : null;
                    })()}
                    {group.label}
                    <span className="section-tab-count">{group.devices.length}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Device sections ── */}
          {groupedDevices.length > 0 ? (
            groupedDevices.map((group, i) => (
              <div
                key={group.key}
                className="device-section"
                ref={el => { sectionRefs.current[i] = el; }}
              >
                {/* Section header chip — only shown when there are multiple groups */}
                {groupedDevices.length > 1 && (
                  <div className="device-section-header">
                    <div className="device-section-chip">
                      {(() => {
                        const ownerUsername = group.devices[0]?.owner_username;
                        const logo = ownerUsername && ownerLogoMap[ownerUsername];
                        return logo ? (
                          <img src={logo} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'contain', background: 'rgba(255,255,255,0.9)', flexShrink: 0 }} />
                        ) : null;
                      })()}
                      <span className="section-chip-name">{group.label}</span>
                      <span className="section-chip-count">{group.devices.length}</span>
                      {user?.is_admin && (
                        <button
                          className="section-chip-x"
                          title="Filter to this owner only"
                          onClick={() => handleOwnerFilterClick(
                            group.key === 'zzz_no_owner' ? '' : group.key
                          )}
                        >×</button>
                      )}
                    </div>
                  </div>
                )}

                <div className="devices-grid">
                  {group.devices.map(device => (
                    <DeviceCard
                      key={device.device_name}
                      device={device}
                      user={user}
                      isHighlighted={highlightBranchId != null && device.branch_id === highlightBranchId}
                      onThumbnailClick={handleThumbnailClick}
                      onRowClick={handleUserPlayerClick}
                      onDeviceNameClick={handleDeviceNameClick}
                      truncateFilename={truncateFilename}
                      formatDate={formatDate}
                      formatMediaTime={formatMediaTime}
                      formatBytes={formatBytes}
                      calculateStoragePercentage={calculateStoragePercentage}
                      getMediaType={getMediaType}
                      showActionsMenu={showActionsMenu}
                      setShowActionsMenu={setShowActionsMenu}
                      filteredDevices={filteredDevices}
                      setDeviceIndex={setDeviceIndex}
                      setSelectedDeviceState={setSelectedDeviceState}
                      setShowDeviceModal={setShowDeviceModal}
                      setIsEditingDevice={setIsEditingDevice}
                      setIsAddingDevice={setIsAddingDevice}
                      onOwnerFilterClick={handleOwnerFilterClick}
                      ownerLogoMap={ownerLogoMap}
                    />
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="no-devices-message">
              <p>No devices match the current filter.</p>
            </div>
          )}

          <p style={{ textAlign: 'center', marginTop: '20px', color: '#666', fontSize: '0.9em' }}>
            {filteredDevices.length} device(s) found. Data updates in real-time via WebSocket.
          </p>
        </>
      )}

      {/* ── Table view: same group structure as cards (sticky owner tabs
            still work because we keep .device-section + sectionRefs) and a
            single portal-rendered kebab menu. ── */}
      {!isLoading && !error && playersViewMode === 'table' && (() => {
        const isAdmin = !!user?.is_admin;
        const MENU_W = 220;
        const MENU_H_EST = 320;
        const openMenuAt = (device, x, y) => {
          const vw = window.innerWidth;
          const vh = window.innerHeight;
          let top  = y + 4;
          let left = x;
          if (top + MENU_H_EST > vh - 8) top = Math.max(8, y - MENU_H_EST - 4);
          if (left < 8) left = 8;
          if (left + MENU_W > vw - 8) left = vw - MENU_W - 8;
          setOpenPlayerRowMenuId(device.device_name);
          setOpenPlayerRowMenuPos({ top, left });
        };

        const openMenuDevice = openPlayerRowMenuId
          ? filteredDevices.find(d => d.device_name === openPlayerRowMenuId)
          : null;

        const openInfo = (d) => {
          const idx = filteredDevices.findIndex(x => x.device_name === d.device_name);
          setDeviceIndex(idx);
          setSelectedDeviceState(d);
          setIsEditingDevice(false);
          setIsAddingDevice(false);
          setShowDeviceModal(true);
        };

        const renderMenuPortal = () => {
          if (!openMenuDevice || !openPlayerRowMenuPos) return null;
          const d = openMenuDevice;
          return createPortal(
            <div
              className="ply-row-menu"
              style={{
                position: 'fixed',
                top:  openPlayerRowMenuPos.top,
                left: openPlayerRowMenuPos.left,
                width: MENU_W,
                background: '#fff',
                border: '1px solid #E5E7EB', borderRadius: 10,
                boxShadow: '0 10px 28px rgba(0,0,0,0.18)',
                zIndex: 100000, overflow: 'hidden',
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {/* Header: device name (non-clickable label). */}
              <div className="ap-row-menu-header" title={d.device_uid || d.device_name}>
                <span className="ap-row-menu-header__dot" style={{ background: d.is_online ? '#2f80ed' : '#dc3545' }} />
                <span className="ap-row-menu-header__name">{d.device_name}</span>
              </div>
              <button type="button" className="ap-row-menu-item"
                onClick={() => { closePlayerRowMenu(); handleDeviceNameClick(d); }}>
                🎮 Remote control
              </button>
              {isAdmin && (
                <button type="button" className="ap-row-menu-item"
                  onClick={() => { closePlayerRowMenu(); handleOpenPlayerManager(d); }}>
                  📋 Player Manager
                </button>
              )}
              <button type="button" className="ap-row-menu-item"
                onClick={() => { closePlayerRowMenu(); handleUserPlayerClick(d); }}>
                📁 Media files
              </button>
              {isAdmin && (
                <button type="button" className="ap-row-menu-item"
                  onClick={() => { closePlayerRowMenu(); handleEditDevice(d); }}>
                  ✏️ Edit
                </button>
              )}
              {isAdmin && (
                <button type="button" className="ap-row-menu-item"
                  onClick={() => { closePlayerRowMenu(); handleDuplicateDevice(d); }}>
                  📄 Duplicate
                </button>
              )}
              {isAdmin && (
                <button type="button" className="ap-row-menu-item"
                  onClick={() => { closePlayerRowMenu(); handleDelete(d.device_name); }}
                  style={{ color: '#B91C1C' }}>
                  🗑 Delete
                </button>
              )}
              <div style={{ height: 1, background: '#F1F5F9', margin: '2px 0' }} />
              <button type="button" className="ap-row-menu-item"
                onClick={() => { closePlayerRowMenu(); openInfo(d); }}>
                ℹ️ Info
              </button>
            </div>,
            document.body
          );
        };

        // Thumbnail JSX is inlined inside renderTableRow (NOT wrapped in a
        // sub-component) — wrapping it in a sub-component defined inside
        // this IIFE made React see a new component identity every render,
        // which unmounted+remounted the cached thumbnail and produced the
        // shimmer flash on every device update.

        // Branch label shown in the Branch column. Prefer the populated
        // device.branch_name field; otherwise fall back to the path-derived
        // branch (same logic the card-view groups use), with placeholder
        // tokens 'aaa_main' / 'zzz_no_folder' translated to friendly text.
        const branchLabelFor = (d) => {
          if (d.branch_name) return d.branch_name;
          const raw = extractBranchName(d);
          if (raw === 'aaa_main') return 'Main';
          if (raw === 'zzz_no_folder') return null;
          return raw.charAt(0).toUpperCase() + raw.slice(1).replace(/-/g, ' ');
        };

        const renderTableRow = (d) => {
          const online = !!d.is_online;
          const accent = online ? '#2f80ed' : '#dc3545';
          // Row tint: light blue for online; offline rows stay white so the
          // row's red status circle does the talking instead of the bg.
          const rowBg = online ? 'rgba(47,128,237,0.14)' : '#fff';
          const lastSeen = d.last_seen ? new Date(d.last_seen) : null;
          // Date-only string for narrow viewports; full date+time for wide ones.
          const lastSeenDateOnly = lastSeen && !isNaN(lastSeen.getTime())
            ? `${String(lastSeen.getDate()).padStart(2,'0')}/${String(lastSeen.getMonth()+1).padStart(2,'0')}/${lastSeen.getFullYear()}`
            : '—';
          const lastSeenFull = lastSeen && !isNaN(lastSeen.getTime())
            ? `${lastSeenDateOnly} · ${lastSeen.toLocaleTimeString('en-US', { hour:'2-digit', minute:'2-digit', hour12:true })}`
            : '—';
          // Title attribute always shows the full timestamp on hover.
          const lastSeenTxt = lastSeenFull;
          const nowPlaying = d.last_displayed_file
            ? truncateFilename(d.last_displayed_file, 32)
            : (d.fallback_file ? truncateFilename(d.fallback_file, 32) : '—');
          const free  = d.storage_space_free;
          const total = d.storage_space_total;
          const pct   = (free != null && total != null && total > 0) ? calculateStoragePercentage(free, total) : null;
          return (
            <tr key={d.device_name}
              className="ap-row ply-row"
              data-online={online ? '1' : '0'}
              style={{ background: rowBg }}
              title="Click for menu · double-click to open remote control"
              onClick={(e) => { if (e.detail >= 2) return; openMenuAt(d, e.clientX, e.clientY); }}
              onDoubleClick={() => { closePlayerRowMenu(); handleDeviceNameClick(d); }}
            >
              {/* Player: thumbnail + name + UID. Thumbnail JSX is inlined
                  (no sub-component wrapper) so React keeps the same
                  CachedGridThumbnail instance across re-renders. Key is
                  device_name only — the component re-fetches internally
                  when last_displayed_file changes, no remount/flash. */}
              <td className="ap-td">
                <div className="ap-user-cell">
                  {(() => {
                    const hasLastDisplayed = !!d.last_displayed_file;
                    const hasFallback = !!d.fallback_file;
                    const hasMedia = (hasLastDisplayed || hasFallback) && !!d.media_files_link;
                    if (!hasMedia) return <div className="ply-row-thumb-empty">📺</div>;
                    const fileToUse = hasLastDisplayed
                      ? d.last_displayed_file
                      : (d.fallback_file_path || d.fallback_file);
                    return (
                      <CachedGridThumbnail
                        itemPath={fileToUse}
                        /* isVideo/isPdf forced false — the API still renders
                           the right frame from the video file; turning these
                           off just hides the play / PDF badge overlays so the
                           thumbnail content stays fully visible. */
                        isVideo={false}
                        isPdf={false}
                        size="list"
                        customBaseUrl={`${window.location.protocol}//${window.location.hostname}`}
                        deviceName={d.device_name}
                      />
                    );
                  })()}
                  <div className="ap-user-name">
                    <div className="ap-user-line1">
                      <span className="ap-username">{d.device_name}</span>
                    </div>
                    {d.description && (
                      <div className="ap-user-line2" style={{ fontSize: 11 }}>
                        {d.description}
                      </div>
                    )}
                  </div>
                </div>
              </td>
              {/* Owner (admin only) — shows the owner's brand logo (same one
                  used by the green section pill) instead of a generic 👤
                  icon. On mobile the big status circle is rendered INSIDE
                  this cell, BEFORE the owner avatar, so the circles line up
                  on the left edge of the column. */}
              {isAdmin && (
                <td className="ap-td ap-td--owner">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    {isMobileView && (
                      <span
                        title={lastSeenTxt}
                        style={{
                          display: 'inline-block',
                          width: 18,
                          height: 18,
                          borderRadius: '50%',
                          backgroundColor: accent,
                          border: '2px solid #ffffff',
                          boxShadow: `0 0 0 2px ${accent}`,
                          flexShrink: 0,
                          boxSizing: 'border-box',
                        }}
                      />
                    )}
                    {d.owner_username ? (
                      <span className="ap-parent" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {ownerLogoMap[d.owner_username] ? (
                          <img
                            src={ownerLogoMap[d.owner_username]}
                            alt=""
                            style={{
                              width: 22, height: 22, borderRadius: '50%',
                              objectFit: 'contain', background: '#fff',
                              border: '1px solid #E5E7EB', flexShrink: 0,
                            }}
                          />
                        ) : (
                          <span style={{
                            width: 22, height: 22, borderRadius: '50%',
                            background: '#F1F5F9', color: '#475569',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700, flexShrink: 0,
                            border: '1px solid #E5E7EB',
                          }}>
                            {(d.owner_username || '?').slice(0, 1).toUpperCase()}
                          </span>
                        )}
                        <span>{d.owner_username}</span>
                      </span>
                    ) : (
                      <span style={{ color: '#CBD5E1' }}>—</span>
                    )}
                  </span>
                </td>
              )}
              {/* Branch — hidden entirely on mobile. */}
              {!isMobileView && (
                <td className="ap-td">
                  {(() => {
                    const b = branchLabelFor(d);
                    return b
                      ? <span style={{ fontSize: 12.5, color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 200 }}>📍 {b}</span>
                      : <span style={{ color: '#CBD5E1' }}>—</span>;
                  })()}
                </td>
              )}
              {/* Status — desktop only. Left-aligned so every row's circle
                  sits at the same x-position, forming a clean vertical line
                  down the column. Mobile circle is inside the Owner cell. */}
              {!isMobileView && (
                <td className="ap-td--lastseen" style={{
                  textAlign: 'left',
                  padding: '8px 12px',
                  verticalAlign: 'middle',
                  borderBottom: '1px solid #F1F5F9',
                  whiteSpace: 'nowrap',
                }}>
                  <div
                    title={lastSeenFull}
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      backgroundColor: accent,
                      display: 'inline-block',
                      border: '2px solid #ffffff',
                      boxShadow: `0 0 0 2px ${accent}, 0 2px 6px rgba(0,0,0,0.20)`,
                      verticalAlign: 'middle',
                      boxSizing: 'border-box',
                    }}
                  />
                  <span
                    title={lastSeenFull}
                    style={{
                      marginLeft: 12, fontSize: 11.5,
                      color: online ? '#047857' : '#B91C1C',
                      fontWeight: 600,
                      verticalAlign: 'middle',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <span className="ap-lastseen-full">{lastSeenFull}</span>
                    <span className="ap-lastseen-date">{lastSeenDateOnly}</span>
                  </span>
                </td>
              )}
              {/* Now playing — hidden on mobile (JSX) and tablet (CSS).
                  On medium screens the long filenames overlapped the
                  Status / Storage columns; .ap-td--nowplaying class lets
                  the CSS hide it between 641px and 1100px. */}
              {!isMobileView && (
                <td className="ap-td ap-td--nowplaying" style={{ maxWidth: 220 }}>
                  <span style={{ fontSize: 12, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 200 }} title={d.last_displayed_file || d.fallback_file || ''}>
                    {nowPlaying}
                  </span>
                </td>
              )}
              {/* Storage — hidden on mobile. */}
              {!isMobileView && (
                <td className="ap-td">
                  {pct === null ? (
                    <span style={{ color: '#CBD5E1' }}>—</span>
                  ) : (
                    <div className="storage-info-card" style={{ minWidth: 140, maxWidth: 200 }}>
                      <div className="storage-text-line">
                        <span className="storage-free">{formatBytes(free)} Free</span>
                        <span className="storage-total">{formatBytes(total)} Total</span>
                      </div>
                      <div className="storage-bar-container">
                        <div
                          className={`storage-bar-used ${pct > 80 ? 'storage-bar-low' : ''}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  )}
                </td>
              )}
              {/* Kebab cell removed — row click opens the menu directly. */}
            </tr>
          );
        };

        if (groupedDevices.length === 0) {
          return (
            <div className="no-devices-message">
              <p>No devices match the current filter.</p>
            </div>
          );
        }

        return (
          <>
            {/* ── Sticky section tabs (shared with cards view) ── */}
            {groupedDevices.length > 1 && (
              <div className="device-section-tabs" ref={tabsBarRef}>
                <div
                  className="tabs-scroll-area"
                  ref={(el) => {
                    scrollerRef.current = el;
                    if (!el || el._dragWired) return;
                    el._dragWired = true;
                    let down = false, startX = 0, startSL = 0, moved = 0;
                    const onMove = (ev) => {
                      if (!down) return;
                      const dx = ev.clientX - startX;
                      if (Math.abs(dx) > 2) {
                        moved = Math.max(moved, Math.abs(dx));
                        dragRef.current.moved = moved;
                        el.scrollLeft = startSL - dx;
                        ev.preventDefault();
                      }
                    };
                    const onUp = () => {
                      down = false;
                      el.classList.remove('dragging');
                      window.removeEventListener('mousemove', onMove);
                      window.removeEventListener('mouseup', onUp);
                    };
                    el.addEventListener('mousedown', (e) => {
                      if (e.button !== 0) return;
                      down = true;
                      startX = e.clientX;
                      startSL = el.scrollLeft;
                      moved = 0;
                      dragRef.current.moved = 0;
                      el.classList.add('dragging');
                      window.addEventListener('mousemove', onMove);
                      window.addEventListener('mouseup', onUp);
                    });
                    el.addEventListener('wheel', (e) => {
                      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                        e.preventDefault();
                        el.scrollLeft += e.deltaY;
                      }
                    }, { passive: false });
                  }}
                  onClickCapture={onTabsBarClickCapture}
                >
                  {groupedDevices.map((group, i) => (
                    <button
                      key={group.key}
                      ref={el => { tabButtonRefs.current[i] = el; }}
                      className={`section-tab-btn${activeTabIndex === i ? ' active' : ''}`}
                      onClick={() => scrollToSection(i)}
                    >
                      {(() => {
                        const ownerUsername = group.devices[0]?.owner_username;
                        const logo = ownerUsername && ownerLogoMap[ownerUsername];
                        return logo ? (
                          <img src={logo} alt="" style={{ width: 18, height: 18, borderRadius: '50%', objectFit: 'contain', background: '#fff', flexShrink: 0, verticalAlign: 'middle', marginRight: 2 }} />
                        ) : null;
                      })()}
                      {group.label}
                      <span className="section-tab-count">{group.devices.length}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── ONE table for ALL groups. ── A single sticky <thead>
                at the very top, then a <tbody> per owner group preceded
                by a "group header" row that names the owner so admins can
                visually separate each owner's run of rows. ── */}
            <div className="ap-table-wrap ap-table-wrap--players">
              <table className="ap-table ap-table--players">
                <thead>
                  <tr>
                    <th>Player</th>
                    {isAdmin && <th>Owner</th>}
                    {!isMobileView && <th>Branch</th>}
                    {!isMobileView && <th>Status · Last seen</th>}
                    {!isMobileView && <th className="ap-th--nowplaying">Now playing</th>}
                    {!isMobileView && <th>Storage</th>}
                  </tr>
                </thead>
                {groupedDevices.map((group, i) => {
                  // Sort inside the group by branch (derived) name then device name.
                  const sortedGroup = [...group.devices].sort((a, b) => {
                    const ba = (branchLabelFor(a) || '').toLowerCase();
                    const bb = (branchLabelFor(b) || '').toLowerCase();
                    if (ba !== bb) return ba.localeCompare(bb);
                    return (a.device_name || '').toLowerCase().localeCompare((b.device_name || '').toLowerCase());
                  });
                  // Visible columns vary by viewport. Kebab column removed —
                  // row click opens the menu instead.
                  //   Desktop: Player, [Owner], Branch, Status, Now playing, Storage
                  //   Mobile:  Player, [Owner]   (status circle lives inside Owner cell)
                  const colSpan = isMobileView
                    ? (1 + (isAdmin ? 1 : 0))
                    : (5 + (isAdmin ? 1 : 0));
                  const ownerUsername = group.devices[0]?.owner_username;
                  const logo = ownerUsername && ownerLogoMap[ownerUsername];
                  return (
                    <tbody
                      key={group.key}
                      ref={el => { sectionRefs.current[i] = el; }}
                    >
                      {groupedDevices.length > 1 && (
                        <tr className="ply-group-row">
                          <td colSpan={colSpan}>
                            <div className="device-section-header" style={{ marginTop: 12, marginBottom: 6 }}>
                              <div className="device-section-chip">
                                {logo && (
                                  <img src={logo} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'contain', background: 'rgba(255,255,255,0.9)', flexShrink: 0 }} />
                                )}
                                <span className="section-chip-name">{group.label}</span>
                                <span className="section-chip-count">{group.devices.length}</span>
                                {user?.is_admin && (
                                  <button
                                    className="section-chip-x"
                                    title="Filter to this owner only"
                                    onClick={() => handleOwnerFilterClick(
                                      group.key === 'zzz_no_owner' ? '' : group.key
                                    )}
                                  >×</button>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      {sortedGroup.map(renderTableRow)}
                    </tbody>
                  );
                })}
              </table>
            </div>

            <p style={{ textAlign: 'center', marginTop: '20px', color: '#666', fontSize: '0.9em' }}>
              {filteredDevices.length} device(s) found. Data updates in real-time via WebSocket.
            </p>
            {renderMenuPortal()}
          </>
        );
      })()}

      {/* Device Details Modal */}
      {showDeviceModal && (
        <div className="device-modal-overlay" onClick={() => setShowDeviceModal(false)}>
          <div className="device-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={() => setShowDeviceModal(false)}></button>
            
            {isAddingDevice && (
              <>
                <h2>Add New Player</h2>
                <div className="device-modal-content">
                  <form className="device-form" onSubmit={(e) => {e.preventDefault(); handleSaveDevice();}}>
                    {/* 2026-05-19 Phase C — three-row Add/Edit Device form.
                        Row 1 (cascading dropdowns): Owner → Brand → Branch.
                          • Brand auto-pins when the owner has exactly one.
                          • Branch dropdown shows "+ Add new branch" inline.
                        Row 2: Name · Description · Media folder (auto-derived
                          from /branch-folder/device-name, editable).
                        Row 3: Register Date · Anydesk ID · Anydesk Password. */}

                    {/* ── Row 1: Owner / Brand / Branch ─────────────────── */}
                    <div className="form-row">
                      <div className="form-group">
                        <label>Owner:</label>
                        <select value={selectedOwner} onChange={handleOwnerChange} className="owner-selector">
                          <option value="">— Select owner —</option>
                          {uniqueOwners.map(owner => (
                            <option key={owner} value={owner}>{owner}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Brand:</label>
                        {availableBrands.length === 1 ? (
                          // Owner has exactly one brand — show it as a
                          // read-only display so admin can see it but can't
                          // re-pick. Hidden input carries the id for the
                          // form submission (deviceFormData.brand_id is
                          // already set by the auto-pin effect).
                          <div style={{ padding: '8px 10px', background: '#F3F4F6', borderRadius: 4, fontSize: 13, color: '#374151' }}>
                            {availableBrands[0].name}
                          </div>
                        ) : (
                          <select
                            value={deviceFormData.brand_id || ''}
                            onChange={e => setDeviceFormData(prev => ({ ...prev, brand_id: e.target.value, branch_id: '' }))}
                            disabled={!selectedOwner || availableBrands.length === 0}
                          >
                            <option value="">{selectedOwner ? '— Select brand —' : 'Pick owner first'}</option>
                            {availableBrands.map(b => (
                              <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Branch:</label>
                        {addingBranch ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input
                              type="text"
                              autoFocus
                              value={newBranchName}
                              onChange={e => setNewBranchName(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreateBranchInline(); } }}
                              placeholder="New branch name"
                              style={{ flex: 1 }}
                            />
                            <button type="button" onClick={handleCreateBranchInline}
                              style={{ padding: '6px 10px', background: '#2196F3', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                              Save
                            </button>
                            <button type="button" onClick={() => { setAddingBranch(false); setNewBranchName(''); }}
                              style={{ padding: '6px 8px', background: '#E5E7EB', color: '#374151', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                              ✕
                            </button>
                          </div>
                        ) : (
                          <select
                            value={deviceFormData.branch_id || ''}
                            onChange={e => {
                              const v = e.target.value;
                              if (v === '__add__') {
                                setAddingBranch(true);
                                return;
                              }
                              setDeviceFormData(prev => ({ ...prev, branch_id: v }));
                            }}
                            disabled={!deviceFormData.brand_id}
                          >
                            <option value="">{deviceFormData.brand_id ? (availableBranches.length === 0 ? 'No branches yet' : '— Main / unassigned —') : 'Pick brand first'}</option>
                            {deviceFormData.brand_id && <option value="__add__">＋ Add new branch…</option>}
                            {availableBranches.map(br => (
                              <option key={br.id} value={br.id}>{br.name}</option>
                            ))}
                          </select>
                        )}
                      </div>
                    </div>

                    {/* ── Row 2: Player Name / Description / Media Folder ─ */}
                    <div className="form-row">
                      <div className="form-group">
                        <label>Player Name:</label>
                        <input
                          type="text"
                          required
                          value={deviceFormData.device_name}
                          onChange={handleDeviceNameChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Description:</label>
                        <input
                          type="text"
                          value={deviceFormData.description}
                          onChange={(e) => setDeviceFormData({...deviceFormData, description: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Media Folder:</label>
                        <input
                          type="text"
                          value={stripOwnerFromPath(deviceFormData.media_files_link)}
                          onChange={(e) => {
                            // Manual edit — turn off auto-derivation so we
                            // don't overwrite the admin's typed value when
                            // device_name / branch later change.
                            setMediaFolderTouched(true);
                            setDeviceFormData(prev => ({
                              ...prev,
                              media_files_link: applyOwnerToPath(e.target.value),
                            }));
                          }}
                          placeholder="/branch-folder/device-name"
                          disabled={!selectedOwner}
                          title={selectedOwner ? `Stored as /${ownerPathSlug}<this-path>` : 'Pick owner first'}
                        />
                      </div>
                    </div>

                    {/* ── Row 3: Register Date / Anydesk ID / Anydesk Pw ── */}
                    <div className="form-row">
                      <div className="form-group">
                        <label>Register Date:</label>
                        <input
                          type="date"
                          value={deviceFormData.register_date ? new Date(deviceFormData.register_date).toISOString().split('T')[0] : ''}
                          onChange={(e) => setDeviceFormData({...deviceFormData, register_date: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Anydesk ID:</label>
                        <input
                          type="text"
                          value={deviceFormData.anydesk_id}
                          onChange={(e) => setDeviceFormData({...deviceFormData, anydesk_id: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Anydesk Password:</label>
                        <div className="password-input-container">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={deviceFormData.anydesk_password}
                            onChange={(e) => setDeviceFormData({...deviceFormData, anydesk_password: e.target.value})}
                          />
                          <span className="password-toggle" onClick={togglePasswordVisibility}>
                            {showPassword ? (
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"></path>
                                <line x1="1" y1="1" x2="23" y2="23"></line>
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="form-buttons">
                      <button type="button" onClick={() => setShowDeviceModal(false)} className="cancel-button">Cancel</button>
                      <button type="submit" disabled={isLoading} className="update-button">
                        {isLoading ? 'Saving...' : 'Save Player'}
                      </button>
                    </div>
                  </form>
                </div>
              </>
            )}
            
            {isEditingDevice && (
              <>
                <h2 className="edit-player-title">
                  Edit Player: {selectedDeviceState?.device_name || deviceFormData.device_name || ''}
                  {/* 2026-05-20 — UID under the name in the same small-grey
                      style as Player Info. After an in-modal adoption swap,
                      we also show a green tick + "Adoption succeeded" until
                      the modal is reopened. */}
                  <div className="edit-player-uid">
                    UID: {selectedDeviceState?.device_uid || '—'}
                    {justAdoptedUid && justAdoptedUid === selectedDeviceState?.device_uid && (
                      <span className="edit-player-adopt-ok" title="Adopt swap completed">
                        &nbsp;✓ Adoption succeeded
                      </span>
                    )}
                  </div>
                </h2>
                <div className="device-modal-content">
                  <form className="device-form" onSubmit={(e) => {e.preventDefault(); handleUpdateDevice();}}>

                    {/* ── Row 1: Owner / Brand / Branch ─────────────────── */}
                    <div className="form-row">
                      <div className="form-group">
                        <label>Owner:</label>
                        <select value={selectedOwner} onChange={handleOwnerChange} className="owner-selector">
                          <option value="">— No owner —</option>
                          {uniqueOwners.map(owner => (
                            <option key={owner} value={owner}>{owner}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Brand:</label>
                        {availableBrands.length === 1 ? (
                          <div style={{ padding: '8px 10px', background: '#F3F4F6', borderRadius: 4, fontSize: 13, color: '#374151' }}>
                            {availableBrands[0].name}
                          </div>
                        ) : (
                          <select
                            value={deviceFormData.brand_id || ''}
                            onChange={e => setDeviceFormData(prev => ({ ...prev, brand_id: e.target.value, branch_id: '' }))}
                            disabled={!selectedOwner || availableBrands.length === 0}
                          >
                            <option value="">{selectedOwner ? '— Select brand —' : 'Pick owner first'}</option>
                            {availableBrands.map(b => (
                              <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                          </select>
                        )}
                      </div>

                      <div className="form-group">
                        <label>Branch:</label>
                        {addingBranch ? (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <input
                              type="text"
                              autoFocus
                              value={newBranchName}
                              onChange={e => setNewBranchName(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreateBranchInline(); } }}
                              placeholder="New branch name"
                              style={{ flex: 1 }}
                            />
                            <button type="button" onClick={handleCreateBranchInline}
                              style={{ padding: '6px 10px', background: '#2196F3', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                              Save
                            </button>
                            <button type="button" onClick={() => { setAddingBranch(false); setNewBranchName(''); }}
                              style={{ padding: '6px 8px', background: '#E5E7EB', color: '#374151', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>
                              ✕
                            </button>
                          </div>
                        ) : (
                          <select
                            value={deviceFormData.branch_id || ''}
                            onChange={e => {
                              const v = e.target.value;
                              if (v === '__add__') { setAddingBranch(true); return; }
                              setDeviceFormData(prev => ({ ...prev, branch_id: v }));
                            }}
                            disabled={!deviceFormData.brand_id}
                          >
                              <option value="">{deviceFormData.brand_id ? (availableBranches.length === 0 ? 'No branches yet' : '— Main / unassigned —') : 'Pick brand first'}</option>
                              {deviceFormData.brand_id && <option value="__add__">＋ Add new branch…</option>}
                              {availableBranches.map(br => (
                                <option key={br.id} value={br.id}>{br.name}</option>
                              ))}
                          </select>
                        )}
                      </div>
                    </div>

                    {/* ── Row 2: Player Name / Description / Media Folder ─ */}
                    <div className="form-row">
                      <div className="form-group">
                        <label>Player Name:</label>
                        <input
                          type="text"
                          required
                          value={deviceFormData.device_name}
                          onChange={handleDeviceNameChange}
                        />
                      </div>

                      <div className="form-group">
                        <label>Description:</label>
                        <input
                          type="text"
                          value={deviceFormData.description}
                          onChange={(e) => setDeviceFormData({...deviceFormData, description: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Media Folder:</label>
                        <input
                          type="text"
                          value={stripOwnerFromPath(deviceFormData.media_files_link)}
                          onChange={(e) => {
                            setMediaFolderTouched(true);
                            setDeviceFormData(prev => ({
                              ...prev,
                              media_files_link: applyOwnerToPath(e.target.value),
                            }));
                          }}
                          placeholder="/branch-folder/device-name"
                          disabled={!selectedOwner}
                          title={selectedOwner ? `Stored as /${ownerPathSlug}<this-path>` : 'Pick owner first'}
                        />
                      </div>
                    </div>

                    {/* ── Row 3: Register Date / Anydesk ID / Anydesk Pw ── */}
                    <div className="form-row">
                      <div className="form-group">
                        <label>Register Date:</label>
                        <input
                          type="date"
                          value={deviceFormData.register_date ? new Date(deviceFormData.register_date).toISOString().split('T')[0] : ''}
                          onChange={(e) => setDeviceFormData({...deviceFormData, register_date: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Anydesk ID:</label>
                        <input
                          type="text"
                          value={deviceFormData.anydesk_id}
                          onChange={(e) => setDeviceFormData({...deviceFormData, anydesk_id: e.target.value})}
                        />
                      </div>

                      <div className="form-group">
                        <label>Anydesk Password:</label>
                        <div className="password-input-container">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={deviceFormData.anydesk_password}
                            onChange={(e) => setDeviceFormData({...deviceFormData, anydesk_password: e.target.value})}
                          />
                          <span className="password-toggle" onClick={togglePasswordVisibility}>
                            {showPassword ? (
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                                <line x1="1" y1="1" x2="23" y2="23"></line>
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-buttons">
                      <div className="navigation-buttons">
                        {/* 2026-05-19 — Adopt selector first, then nav arrows.
                            Pairs this placeholder with an auto-registered orphan UID. */}
                        <select
                          className="adopt-select"
                          value=""
                          disabled={adoptingFromUid || orphanDevices.length === 0}
                          onChange={(e) => { const v = e.target.value; if (v) handleAdoptDevice(v); }}
                          title={orphanDevices.length === 0 ? 'No orphan devices waiting to be adopted' : 'Adopt a real device into this placeholder'}
                        >
                          <option value="">
                            {adoptingFromUid
                              ? 'Adopting…'
                              : orphanDevices.length === 0
                                ? '🔗 No orphans'
                                : `🔗 Adopt (${orphanDevices.length})`}
                          </option>
                          {orphanDevices.map(o => (
                            <option key={o.device_uid} value={o.device_uid}>
                              {o.device_uid}{o.is_online ? ' • online' : ''}{o.device_model ? ` • ${o.device_model}` : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={goToPreviousDevice}
                          disabled={deviceIndex === 0}
                          className="nav-button prev-button"
                        >
                           &lt;
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowDeviceModal(false)}
                          className="cancel-button"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={goToNextDevice}
                          disabled={deviceIndex >= filteredDevices.length - 1}
                          className="nav-button next-button"
                        >
                          &gt;
                        </button>
                      </div>
                      <button 
                        type="submit" 
                        disabled={isLoading} 
                        className="update-button"
                      >
                        {isLoading ? 'Updating...' : 'Update Player'}
                      </button>
                    </div>
                  </form>
                </div>
              </>
            )}
            
            {!isAddingDevice && !isEditingDevice && selectedDeviceState && (
  <>
    <h2>Player Details: {selectedDeviceState.device_name}</h2>
    <button className="close-button" onClick={() => setShowDeviceModal(false)}>x</button>
    <div className="device-modal-content">
      <div className="device-details-grid">

        {/* Player Name */}
        <div className="detail-item">
          <div className="detail-label">Player Name</div>
          <div className={`detail-value ${selectedDeviceState.is_online ? 'online' : 'offline'}`}>
            {selectedDeviceState.device_name}
            {selectedDeviceState.device_uid && selectedDeviceState.device_uid !== selectedDeviceState.device_name && (
              <div style={{ fontSize: 10, color: '#9CA3AF', fontFamily: 'monospace', fontWeight: 400, marginTop: 2 }}>
                {selectedDeviceState.device_uid}
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        <div className="detail-item">
          <div className="detail-label">Description</div>
          <div className="detail-value">
            {(selectedDeviceState.description ?? 'N/A')}
          </div>
        </div>

        {/* Model */}
        <div className="detail-item">
          <div className="detail-label">Model</div>
          <div className="detail-value">
            {(selectedDeviceState.device_model ?? 'N/A')}
          </div>
        </div>

        {/* Android Version */}
        <div className="detail-item">
  <div className="detail-label">Android Ver.</div>
  <div className="detail-value">
    {(selectedDeviceState.android_version ?? 'N/A')}
  </div>
</div>

        {/* Local IP */}
        <div className="detail-item">
  <div className="detail-label">Local IP</div>
  <div className="detail-value">
    {(selectedDeviceState.ip_address_internal ?? 'N/A')}
  </div>
</div>

        {/* Media Folder */}
<div className="detail-item">
  <div className="detail-label">Media Folder</div>
  <div className="detail-value">
    {(() => {
  if (!selectedDeviceState.media_files_link) return 'N/A';
  try {
    const url = new URL(selectedDeviceState.media_files_link);
    if (url.pathname.startsWith('/home/')) {
      // Remove '/home' from the start
      return url.pathname.substring('/home'.length);
    }
    return url.pathname;
  } catch {
    return selectedDeviceState.media_files_link;
  }
})()}
  </div>
</div>

{/* Register Date */}
        <div className="detail-item">
          <div className="detail-label">Register Date</div>
          <div className="detail-value">
            {selectedDeviceState.register_date ? formatDate(selectedDeviceState.register_date) : 'N/A'}
          </div>
        </div>

        {/* Working Hours */}
        <div className="detail-item">
          <div className="detail-label">Working Hours</div>
          <div className="detail-value">
            {(selectedDeviceState.working_hours ?? 'N/A')}
          </div>
        </div>

      </div> {/* End of device-details-grid */}
      
      {user.is_admin && (
        <div className="form-buttons">
            <button type="button" onClick={() => handleEditDevice(selectedDeviceState)} className="edit-button">Edit Player</button>
            {!selectedDeviceState.is_online && (
              <button onClick={() => {setShowDeviceModal(false); handleDelete(selectedDeviceState.device_name);}} className="delete-button">Delete Player</button>
            )}
        </div>
      )}
    </div>
  </>
)}
          </div>
        </div>
      )}
    
{/* Media Preview Modal */}
{showPreviewModal && previewItem && (
  <div className="preview-modal-overlay" onClick={() => setShowPreviewModal(false)}>
    <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
      {/* Header: filename + close button */}
      <div className="preview-header">
        <h2>{previewItem.filename}</h2>
        <button className="preview-close-button" onClick={() => setShowPreviewModal(false)}>×</button>
      </div>
      
      <div className="preview-content">
        {previewItem.type === 'image' && (
          <img 
            src={previewItem.url} 
            alt="Preview"
            className="preview-image"
            onLoad={(e) => {
              console.log('? Preview image loaded successfully');
              console.log(`?? Image dimensions: ${e.target.naturalWidth}x${e.target.naturalHeight}`);
              console.log(`?? Display dimensions: ${e.target.width}x${e.target.height}`);
            }}
            onError={(e) => {
              console.log('? Preview image failed to load, trying fallback');
              e.target.src = `${window.location.protocol}//${window.location.hostname}${previewItem.pathForFetch}`;
            }}
          />
        )}
        
        {previewItem.type === 'video' && (
          <div className="video-preview-container">
            <VideoPreview 
              pathForFetch={previewItem.pathForFetch} 
              filename={previewItem.filename}
              deviceName={previewItem.deviceName}
              className="preview-video"
              isModal={true}
            />
          </div>
        )}
        
{previewItem.type === 'pdf' && (
  <div className="preview-pdf-container">
    <iframe 
      src={`${previewItem.url}?thumbnail=true`}
      title="PDF Preview" 
      className="preview-pdf"
      onLoad={() => console.log('? PDF iframe loaded successfully')}
      onError={() => console.error('? PDF iframe failed to load')}
    />
  </div>
)}
        
        {previewItem.type === 'unknown' && (
          <div className="preview-unknown">
            <p>Preview not available for this file type.</p>
          </div>
        )}
      </div>
    </div>
  </div>
)}

{/* Remote Control Modal */}
      {/* Standalone Player Manager — opened directly from kebab/card menus
          (admin only). Does NOT spawn the Remote Control modal first. */}
      {quickManageDevice && (
        <DeviceQuickManageModal
          device={quickManageDevice}
          isAdmin={!!user?.is_admin}
          onClose={() => setQuickManageDevice(null)}
          onDeviceChanged={(patch) => {
            try { fetchDevicesOptimized(); } catch (_) {}
            if (patch && typeof patch === 'object') {
              setQuickManageDevice(prev => prev ? { ...prev, ...patch } : prev);
            }
          }}
        />
      )}

      {showRemoteControlModal && selectedDeviceForRemote && (
        <RemoteControlModal
          device={selectedDeviceForRemote}
          onClose={handleCloseRemoteControl}
          user={user}
          onDeviceChanged={(patch) => {
            // 2026-05-20 — Bubble-up from File Sync (name / MQTT-token /
            // media_files_link change). Refresh the dashboard list so the
            // device card moves to its new branch group and the Edit
            // Player modal opens with fresh data on its next click.
            try { fetchDevicesOptimized(); } catch (_) {}
            // Patch the locally-held device object so the still-open
            // Remote Control modal also reads the new value without
            // waiting for the dashboard fetch round-trip.
            if (patch && typeof patch === 'object') {
              setSelectedDeviceForRemote(prev => prev ? { ...prev, ...patch } : prev);
            }
          }}
        />
      )}
	  
	  {/* File Manager Modal */}
{deviceForFileManagerModal && (
  <div className="file-manager-modal-overlay" onClick={() => setDeviceForFileManagerModal(null)}>
    <div className="file-manager-modal-content" onClick={(e) => e.stopPropagation()}>
      <button 
        className="file-manager-modal-close-button" 
        onClick={() => setDeviceForFileManagerModal(null)}
      >
        &times;
      </button>
      <FileManager
        key={deviceForFileManagerModal.device_name} // Add key to re-mount on device change
        selectedDevice={deviceForFileManagerModal}
        isEmbedded={true} // Important for styling and behavior
        showTitle={`File Manager: ${deviceForFileManagerModal.device_name}`}
        deviceName={deviceForFileManagerModal.device_name} // Pass device name for thumbnails
        initialPath={(() => {
          const link = deviceForFileManagerModal?.media_files_link;
          const deviceName = deviceForFileManagerModal?.device_name;
          let finalPath = '/';
          if (!link) {
            if (deviceName) finalPath = `/home/${deviceName}/`;
            else console.error("FileManager Modal: Cannot determine initialPath - missing link and device name.");
            return finalPath;
          }
          try {
            const url = new URL(link);
            let extractedPath = url.pathname;
            // Ensure it starts with a single slash and includes '/home/' if it's part of the structure
            if (extractedPath.startsWith('/home/')) {
               // Path is like /home/user/device/
               finalPath = '/' + extractedPath.split('/').filter(p => p && p !== 'home').join('/');
               if (!finalPath.endsWith('/') && finalPath !== '/') finalPath += '/';
               finalPath = `/home${finalPath}`; // Prepend /home back
            } else if (deviceName) {
               // Path might be just /device/ or similar, prefix with /home/user/
               // This part is tricky without knowing the exact structure of media_files_link
               // Assuming media_files_link gives the path segment after /home/
               // For now, let's assume the link itself provides the correct relative path for WebDAV
               finalPath = '/' + extractedPath.split('/').filter(Boolean).join('/');
            }

            if (!finalPath.endsWith('/')) finalPath += '/';
            // Ensure path structure is like /home/ACTUAL_USERNAME_OR_DEVICE_AS_USER/DEVICE_NAME/
            // The FileManager will use its own logic for base paths (/home/user/ or /home/admin/)
            // We need to provide the part of the path *after* the user's root or the admin's /home/
            // The simplest is to provide the full expected WebDAV path.
            
            // If media_files_link is "https://[DOMAIN]/home/some_user/some_device",
            // then initialPath for FileManager should be "/home/some_user/some_device/"
            finalPath = url.pathname; 
            if (!finalPath.endsWith('/')) finalPath += '/';

            console.log(`FileManager Modal initialPath from link "${link}": ${finalPath}`);
          } catch (e) {
            console.error(`Error parsing media_files_link URL for modal: ${link}`, e);
            if (deviceName) finalPath = `/home/${deviceName}/`; // Fallback
            console.warn(`Fallback path for modal constructed using device name: ${finalPath}`);
          }
          return finalPath;
        })()}
        customBaseUrl={`${window.location.protocol}//${window.location.hostname}`}
        // maxRows is not typically used for modal display, can be removed or ignored by FileManager in modal mode
      />
    </div>
  </div>
)}

{false && showGroupManager && (
  <GroupManager user={user} onClose={() => setShowGroupManager(false)} />
)}

	  {/* Scroll to Top Button */}
{showScrollTop && (
  <button 
    onClick={scrollToTop}
    className="scroll-to-top-btn"
    title="Back to top"
  >
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="19" x2="12" y2="5"></line>
      <polyline points="5 12 12 5 19 12"></polyline>
    </svg>
  </button>
)}

{/* Global menu dropdown that's positioned absolutely - STABLE VERSION */}
{showActionsMenu && (
  <div 
    className="actions-menu-dropdown"
    style={{
      position: 'fixed',
      zIndex: 999999,
      // Position will be set by JavaScript
    }}
  >
    {(() => {
      const device = devices.find(d => d.device_name === showActionsMenu);
      
      if (!device) {
        // If device not found, close menu
        setTimeout(() => setShowActionsMenu(null), 0);
        return null;
      }

      // Helper: open the Info modal for this device.
      const openDeviceInfo = (e) => {
        e.stopPropagation();
        const index = filteredDevices.findIndex(d => d.device_name === device.device_name);
        setDeviceIndex(index);
        setSelectedDeviceState(device);
        setShowDeviceModal(true);
        setIsEditingDevice(false);
        setIsAddingDevice(false);
        setShowActionsMenu(null);
      };

      return (
        <React.Fragment>
          {/* For admins, the top header opens Player Manager. For everyone
              else it keeps the original "click to view info" behaviour. */}
          {user.is_admin ? (
            <div
              className={`device-name-header ${device.is_online ? 'online' : 'offline'} clickable-device-header`}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenPlayerManager(device);
              }}
              title="Open Player Manager"
            >
              📋 Player Manager
            </div>
          ) : (
            <div
              className={`device-name-header ${device.is_online ? 'online' : 'offline'} clickable-device-header`}
              onClick={openDeviceInfo}
              title="Click to view device information"
            >
              {device.device_name}
            </div>
          )}

          {/* Admin-only actions */}
          {user.is_admin && (
            <>
              <div
                className="actions-menu-item edit"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditDevice(device);
                  setShowActionsMenu(null);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
                Edit
              </div>

              <div
                className="actions-menu-item duplicate"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDuplicateDevice(device);
                  setShowActionsMenu(null);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                Duplicate
              </div>

              <div
                className="actions-menu-item delete"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(device.device_name);
                  setShowActionsMenu(null);
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
                Delete
              </div>

              {/* Info moved to the end for admins (top slot now hosts Player Manager). */}
              <div
                className="actions-menu-item info"
                onClick={openDeviceInfo}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
                Info
              </div>
            </>
          )}
	  </React.Fragment>
      );
    })()}
  </div>
)}
    </div>
    </Sidebar>
  );
}

export default App;
